import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  User,
  setPersistence,
  browserLocalPersistence,
  updateEmail as firebaseUpdateEmail,
  updatePassword as firebaseUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signInWithCustomToken,
  type ActionCodeSettings,
} from "firebase/auth";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  Timestamp,
  writeBatch,
  FieldValue,
  arrayUnion,
  arrayRemove,
  onSnapshot
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { secureLog } from '../lib/errors/logger';
import { verifyUserIdOwnership } from '../lib/security/idorProtection';
import { UserModel, ProductCategory, Conversation, Message, UserVoucher, Voucher, Product, Category } from '../types';
import { validatePassword, isValidDocumentId, validateString } from '../utils/validation';

import { sanitizeString, sanitizeEmail, sanitizeError } from '../utils/sanitization';
import { expandSearchQuery, sanitizeSearchQuery, isValidSearchQuery } from '../utils/searchExpand';
import { isValidImageUrl } from '../utils/imageValidation';

function validateFile(file: File): { valid: boolean; error?: string } {
  // Security: File size limit (10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return { valid: false, error: 'File size must be less than 10MB' };
  }

  // Security: Allowed file types
  const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const allowedFileTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain'
  ];
  
  const allowedTypes = [...allowedImageTypes, ...allowedFileTypes];
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'File type not allowed' };
  }

  // Security: Check file extension matches MIME type
  const extension = file.name.split('.').pop()?.toLowerCase();
  const mimeToExtension: Record<string, string[]> = {
    'image/jpeg': ['jpg', 'jpeg'],
    'image/png': ['png'],
    'image/gif': ['gif'],
    'image/webp': ['webp'],
    'application/pdf': ['pdf'],
    'application/msword': ['doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
    'text/plain': ['txt']
  };

  if (extension && mimeToExtension[file.type] && !mimeToExtension[file.type].includes(extension)) {
    return { valid: false, error: 'File extension does not match file type' };
  }

  return { valid: true };
}


// Security: Enhanced rate limiting for Firebase operations
class FirebaseRateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts = 20; // 20 operations per minute
  private readonly windowMs = 60000; // 1 minute
  private readonly maxAttemptsPerHour = 200; // 200 operations per hour
  private readonly hourlyWindowMs = 3600000; // 1 hour

  isAllowed(key: string): { allowed: boolean; message?: string } {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    const hourlyAttempts = attempts.filter(time => now - time < this.hourlyWindowMs);
    
    // Check hourly limit first
    if (hourlyAttempts.length >= this.maxAttemptsPerHour) {
      return {
        allowed: false,
        message: 'Too many operations in the last hour. Please wait before trying again.'
      };
    }
    
    if (recentAttempts.length >= this.maxAttempts) {
      return {
        allowed: false,
        message: 'Too many operations. Please try again later.'
      };
    }
    
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    return { allowed: true };
  }

  getRemainingAttempts(key: string): number {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    return Math.max(0, this.maxAttempts - recentAttempts.length);
  }
}

const firebaseRateLimiter = new FirebaseRateLimiter();

/**
 * Firebase Configuration for Baakas E-commerce
 *
 * PREREQUISITES (Firebase Console Setup):
 * 1. Authentication > Sign-in method > Email/Password provider enabled
 * 2. Authentication > Settings > Authorized domains include your production domain
 * 3. Authentication > Templates > Password reset email template configured
 * 4. Project Settings > General > Web app configuration matches environment variables
 *
 * ENVIRONMENT VARIABLES REQUIRED:
 * - REACT_APP_FIREBASE_API_KEY
 * - REACT_APP_FIREBASE_AUTH_DOMAIN
 * - REACT_APP_FIREBASE_PROJECT_ID
 * - REACT_APP_FIREBASE_STORAGE_BUCKET
 * - REACT_APP_FIREBASE_MESSAGING_SENDER_ID
 * - REACT_APP_FIREBASE_APP_ID
 */

/**
 * PRODUCTION FIREBASE CONFIGURATION
 * 
 * SECURITY: All Firebase credentials MUST come from environment variables.
 * No hardcoded credentials are allowed in the codebase.
 * 
 * PRODUCTION REQUIREMENTS:
 * - All environment variables MUST be set at build time
 * - Build will fail if required variables are missing
 * - No fallback values - strict validation only
 * 
 * Required Environment Variables:
 * - REACT_APP_FIREBASE_API_KEY
 * - REACT_APP_FIREBASE_AUTH_DOMAIN
 * - REACT_APP_FIREBASE_PROJECT_ID
 * - REACT_APP_FIREBASE_STORAGE_BUCKET
 * - REACT_APP_FIREBASE_MESSAGING_SENDER_ID
 * - REACT_APP_FIREBASE_APP_ID
 * - REACT_APP_FIREBASE_MEASUREMENT_ID (optional, for Analytics)
 */

const missingEnvVars: string[] = [];

const readEnv = (key: string): string => {
  const value = process.env[key]?.trim() ?? "";
  if (!value) {
    missingEnvVars.push(key);
    secureLog('error', `Missing required Firebase environment variable: ${key}`);
  }
  return value;
};

// Build-time validation: All environment variables are required
const firebaseConfig = {
  apiKey: readEnv("REACT_APP_FIREBASE_API_KEY"),
  authDomain: readEnv("REACT_APP_FIREBASE_AUTH_DOMAIN"),
  projectId: readEnv("REACT_APP_FIREBASE_PROJECT_ID"),
  storageBucket: readEnv("REACT_APP_FIREBASE_STORAGE_BUCKET"),
  messagingSenderId: readEnv("REACT_APP_FIREBASE_MESSAGING_SENDER_ID"),
  appId: readEnv("REACT_APP_FIREBASE_APP_ID"),
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID?.trim() || undefined,
};

// Validation: Log errors but don't throw (prevents infinite loops)
// Errors will be caught during Firebase initialization instead
if (missingEnvVars.length > 0) {
  const errorMessage = `Missing required Firebase environment variables: ${missingEnvVars.join(', ')}. ` +
    `Set all REACT_APP_FIREBASE_* environment variables in your deployment platform.`;
  
  secureLog('error', errorMessage);
  (window as any).__FIREBASE_CONFIG_ERROR__ = errorMessage;
  // Don't throw - let Firebase initialization handle it gracefully
}

// Validate all required fields are present
const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
const missingFields = requiredFields.filter(field => {
  const value = firebaseConfig[field as keyof typeof firebaseConfig];
  return !value || (typeof value === 'string' && value.trim() === '');
});

const isConfigValid = missingFields.length === 0;

if (!isConfigValid) {
  const errorMessage = `Missing Firebase configuration fields: ${missingFields.join(', ')}. ` +
    `Set all REACT_APP_FIREBASE_* environment variables in your deployment platform.`;
  
  secureLog('error', errorMessage);
  (window as any).__FIREBASE_CONFIG_ERROR__ = errorMessage;
}

// Initialize Firebase - use valid placeholder config if invalid to prevent path errors
// If config is invalid, use placeholder to prevent "projects//databases" errors
let app: FirebaseApp;
let usingPlaceholderConfig = false;

try {
  // Check if Firebase app already exists
  const existingApps = getApps();
  
  // If config is invalid, always use placeholder (don't use existing invalid app)
  if (!isConfigValid) {
    // Config is invalid - use placeholder to prevent path errors
    // This prevents "projects//databases" errors from empty projectId
    secureLog('error', 'Firebase config invalid - using placeholder (services will not work)');
    
    // Delete any existing invalid apps first
    if (existingApps.length > 0) {
      try {
        // Try to delete existing apps to start fresh with placeholder
        existingApps.forEach(existingApp => {
          try {
            // Can't delete apps in Firebase, but we can use a named placeholder app
          } catch {}
        });
      } catch {}
    }
    
    // Initialize with placeholder config
    app = initializeApp({
      apiKey: 'placeholder-invalid-config',
      authDomain: 'placeholder.firebaseapp.com',
      projectId: 'placeholder-project',
      storageBucket: 'placeholder.appspot.com',
      messagingSenderId: '000000000000',
      appId: '1:000000000000:web:placeholder'
    }, 'placeholder-invalid');
    usingPlaceholderConfig = true;
  } else if (existingApps.length > 0) {
    // Config is valid and app exists - use existing app
    app = existingApps[0];
  } else {
    // Config is valid and no app exists - initialize normally
    app = initializeApp(firebaseConfig);
  }
} catch (error: any) {
  // Handle Firebase initialization errors gracefully
  if (error?.code === 'app/duplicate-app') {
    // App already initialized - check if it's our placeholder or get default
    try {
      app = getApp('placeholder-invalid');
      usingPlaceholderConfig = true;
    } catch {
      app = getApp();
    }
  } else {
    // Log error but don't throw - this prevents infinite loops
    secureLog('error', 'Firebase initialization failed', error?.message || String(error));
    (window as any).__FIREBASE_CONFIG_ERROR__ = 
      `Firebase initialization failed: ${error?.message || 'Unknown error'}. ` +
      `Verify all REACT_APP_FIREBASE_* environment variables are correctly set.`;
    
    // Use placeholder config as last resort
    try {
      app = getApp('placeholder-invalid');
      usingPlaceholderConfig = true;
    } catch {
      app = initializeApp({
        apiKey: 'placeholder-invalid-config',
        authDomain: 'placeholder.firebaseapp.com',
        projectId: 'placeholder-project',
        storageBucket: 'placeholder.appspot.com',
        messagingSenderId: '000000000000',
        appId: '1:000000000000:web:placeholder'
      }, 'placeholder-invalid');
      usingPlaceholderConfig = true;
    }
  }
}

export { app };

// Initialize services - always initialize (even with placeholder, they'll fail when used)
let authInstance: ReturnType<typeof getAuth>;
try {
  authInstance = getAuth(app);
} catch (error: any) {
  secureLog('error', 'Failed to initialize Firebase Auth', error?.message || String(error));
  // If getAuth fails, try placeholder app
  try {
    const placeholderApp = initializeApp({
      apiKey: 'placeholder-invalid-config',
      authDomain: 'placeholder.firebaseapp.com',
      projectId: 'placeholder-project',
      storageBucket: 'placeholder.appspot.com',
      messagingSenderId: '000000000000',
      appId: '1:000000000000:web:placeholder'
    }, 'placeholder-auth');
    authInstance = getAuth(placeholderApp);
    usingPlaceholderConfig = true;
  } catch {
    // Absolute last resort - this shouldn't happen
    throw new Error('Firebase Auth initialization completely failed. Check environment variables.');
  }
}

export const auth = authInstance;

// Default action code settings for email verification and password reset
// Uses production domain from environment variables or current origin
const getActionCodeUrl = (): string => {
  const productionUrl = process.env.REACT_APP_PRODUCTION_URL || process.env.REACT_APP_VERCEL_URL;
  if (productionUrl) {
    // Ensure URL has protocol
    const url = productionUrl.startsWith('http://') || productionUrl.startsWith('https://') 
      ? productionUrl 
      : `https://${productionUrl}`;
    return `${url}/auth/login`;
  }
  // Use current origin as fallback (already has protocol)
  return `${window.location.origin}/auth/login`;
};

export const defaultActionCodeSettings: ActionCodeSettings = {
  url: getActionCodeUrl(),
  handleCodeInApp: true, // true = use in-app AuthActionHandler for reset/verify flows
};

// Log action code URL only when debug flag is enabled
if (process.env.NODE_ENV === 'development' && process.env.VITE_ENABLE_DEBUG_LOGS === 'true') {
  secureLog('info', 'Action code URL configured', {
    url: defaultActionCodeSettings.url,
    handleCodeInApp: defaultActionCodeSettings.handleCodeInApp,
    productionUrl: process.env.REACT_APP_PRODUCTION_URL || process.env.REACT_APP_VERCEL_URL || 'not set',
    currentOrigin: typeof window !== 'undefined' ? window.location.origin : 'N/A'
  });
}

// Security: Configure authentication persistence with error handling
setPersistence(auth, browserLocalPersistence).catch((error) => {
  // Log error but continue - persistence failure is not critical
  secureLog('warn', 'Failed to set auth persistence', error);
  // Continue without persistence - not critical for app functionality
});

// Improve compatibility with privacy/ad blockers by auto-detecting long polling
// Use new Firestore persistence API (FirestoreSettings.cache)
// This replaces the deprecated enableIndexedDbPersistence()
// The new API automatically handles IndexedDB incompatibility and falls back to memory cache
// Initialize Firestore - always initialize (will fail when used if config is invalid)
let dbInstance: ReturnType<typeof initializeFirestore>;
try {
  dbInstance = initializeFirestore(app, {
    // Force long-polling to avoid WebChannel being blocked by extensions / networks
    experimentalForceLongPolling: true,
    // Use new persistence API - supports multiple tabs with synchronization
    // Automatically handles IndexedDB incompatibility by falling back to memory cache
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
      cacheSizeBytes: 40 * 1024 * 1024 // 40MB cache size
    })
  });
} catch (error: any) {
  secureLog('error', 'Failed to initialize Firestore', error?.message || String(error));
  // If using placeholder config, Firestore will fail - that's expected
  // Re-initialize with same app (will fail when used, but won't crash on import)
  dbInstance = initializeFirestore(app, {
    experimentalForceLongPolling: true,
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
      cacheSizeBytes: 40 * 1024 * 1024
    })
  });
}
export const db = dbInstance;

// Initialize Storage - always initialize (will fail when used if config is invalid)
let storageInstance: ReturnType<typeof getStorage>;
try {
  storageInstance = getStorage(app);
} catch (error: any) {
  secureLog('error', 'Failed to initialize Firebase Storage', error?.message || String(error));
  // Re-initialize - will fail when used if config is invalid
  storageInstance = getStorage(app);
}
export const storage = storageInstance;

// Initialize Cloud Functions - always initialize (will fail when used if config is invalid)
let cloudFunctionsInstance: ReturnType<typeof getFunctions>;
try {
  // Initialize Cloud Functions with us-central1 region to match backend deployment
  cloudFunctionsInstance = getFunctions(app, 'us-central1');
} catch (error: any) {
  secureLog('error', 'Failed to initialize Cloud Functions', error?.message || String(error));
  // Re-initialize - will fail when used if config is invalid
  cloudFunctionsInstance = getFunctions(app, 'us-central1');
}
export const cloudFunctions = cloudFunctionsInstance;

// Centralized Firebase helpers
export const uidOrThrow = (): string => {
  const uid = auth.currentUser?.uid;
  if (!uid) {
    throw new Error("User must be authenticated to perform this action");
  }
  return uid;
};

// Database guard utilities for safer writes
export const dbGuard = {
  ensureOwner: <T extends { userId: string }>(obj: T, uid: string): T => {
    if (obj.userId !== uid) {
      throw new Error("You do not have permission to modify this item");
    }
    return obj;
  },

  stampCreate: <T extends Record<string, any>>(
    obj: T
  ): T & { createdAt: FieldValue; updatedAt: FieldValue } => {
    return {
      ...obj,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
  },

  stampUpdate: <T extends Record<string, any>>(
    obj: T
  ): T & { updatedAt: FieldValue } => {
    return {
      ...obj,
      updatedAt: serverTimestamp(),
    };
  },
};

// Collection references - corrected according to FIRESTORE_CONNECTIONS.md
export const PRODUCTS = "Products";
const productsCollection = collection(db, PRODUCTS); // Using actual Firestore collection name
export const usersCollection = collection(db, "Users").withConverter<UserModel>(
  {
    toFirestore: (user: UserModel) => {
      const { id, ...userData } = user;
      return userData;
    },
    fromFirestore: (snapshot, options) => {
      const data = snapshot.data(options);
      return {
        id: snapshot.id,
        ...data,
      } as UserModel;
    },
  }
);
export const ordersCollection = collection(db, "Orders");
export const categoriesCollection = collection(db, "categories");
// Wishlist is stored as subcollection under each user document
// No need for separate wishlistCollection reference
export const rewardsCollection = collection(db, "rewards");

// ProductCategory collection reference
export const productCategoryCollection = collection(db, "ProductCategory");

// Messaging collections
export const conversationsCollection = collection(db, "conversations");

// Presence tracking collections
export const presenceCollection = collection(db, "presence");

// Product interface is now imported from ../types to avoid conflicts

// User interface - corrected according to FIRESTORE_CONNECTIONS.md
export interface UserAddress {
  id: string;
  name: string;
  type: "home" | "work" | "other";
  isDefault: boolean;
  province: string;
  district: string;
  city: string;
  streetAddress: string;
  postalCode: string;
}

export interface UserProfile {
  uid: string; // Firebase Auth UID
  firstName?: string;
  lastName?: string;
  email: string;
  displayName?: string;
  phoneNumber?: string;
  dob?: string;
  gender?: string;
  language?: string;
  timezone?: string;
  bio?: string;
  location?: string;
  photoURL?: string | null;
  role?: "user" | "seller" | "admin";
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// Order interface is now defined in orderService.ts to avoid conflicts

export interface CartItem {
  id?: string;
  productId: string;
  isFavorite: boolean;
  addedAt?: Timestamp;
}

// Rewards interface - corrected according to FIRESTORE_CONNECTIONS.md
export interface UserRewards {
  id?: string;
  userId: string;
  points: number;
  vouchers: Array<{
    id: string;
    code: string;
    discount: number;
    validUntil: Timestamp;
    isUsed: boolean;
  }>;
  giftCards: Array<{
    id: string;
    code: string;
    amount: number;
    isUsed: boolean;
  }>;
  walletBalance: number;
  updatedAt?: Timestamp;
}

// Helper function to map Firestore document to Product
export const productFromSnap = (doc: any): Product => {
  const data = doc.data();

  // Extract colors and sizes from productAttributes (primary source)
  const getAttributeValues = (attributeName: string): string[] => {
    const attribute = (data.productAttributes || []).find(
      (attr: any) =>
        (attr.Name || "").toLowerCase() === attributeName.toLowerCase()
    );
    return attribute?.Values || [];
  };

  const attributeSizes = getAttributeValues("Size");
  const attributeColors = getAttributeValues("Color");

  // Transform variants to consistent structure while maintaining attributeValues
  const transformVariants = (variants: any = []) => {
    if (Array.isArray(variants)) {
      return variants.map((variant, index) => {
        // Handle the actual database structure with attributeValues
        if (variant.attributeValues) {
          return {
            id: variant.id || `variant_${index}`,
            attributeValues: {
              Size:
                variant.attributeValues?.Size ||
                variant.attributeValues?.size ||
                "One Size",
              Color:
                variant.attributeValues?.Color ||
                variant.attributeValues?.color ||
                "Default",
            },
            stockQty: variant.stockQty ?? variant.stockQuantity ?? 0, // Default to 0 if not set (treat missing stock as out of stock)
            priceOverride:
              variant.priceOverride || variant.price || data.price || 0,
            sku:
              variant.sku ||
              `${doc.id}_${variant.attributeValues?.Size || "S"}_${
                variant.attributeValues?.Color || "Default"
              }`,
            isActive: variant.isActive !== false, // Default to true if not specified
            image: isValidImageUrl(variant.image) ? variant.image : "",
            soldQuantity: variant.soldQuantity || 0,
            barcode: variant.barcode || null,
          };
        } else {
          // Fallback for other structures - convert to attributeValues format
          return {
            id: variant.id || `variant_${index}`,
            attributeValues: {
              Size: variant.size || "One Size",
              Color: variant.color || "Default",
            },
            stockQty: variant.stockQty ?? variant.stockQuantity ?? 0, // Default to 0 if not set (treat missing stock as out of stock)
            priceOverride:
              variant.priceOverride || variant.price || data.price || 0,
            sku:
              variant.sku ||
              `${doc.id}_${variant.size || "S"}_${variant.color || "Default"}`,
            isActive: variant.isActive !== false,
            image: isValidImageUrl(variant.image) ? variant.image : "",
            soldQuantity: variant.soldQuantity || 0,
            barcode: variant.barcode || null,
          };
        }
      });
    } else if (variants && typeof variants === "object") {
      // Handle object structure (like {L_Blue: {...}, S_Blue: {...}})
      const variantsArray = [];

      for (const [key, variant] of Object.entries(variants)) {
        const variantData = variant as any;

        // Extract size and color from the key (e.g., "L_Blue" -> size: "L", color: "Blue")
        const keyParts = key.split("_");
        const size = keyParts[0] || "";
        const color = keyParts.slice(1).join("_") || "";

        const transformedVariant = {
          id: variantData.id || key,
          attributeValues: {
            Size: variantData.size || size || "One Size",
            Color: variantData.color || color || "Default",
          },
          stockQty: variantData.stockQty ?? variantData.stockQuantity ?? 0, // Default to 0 if not set (treat missing stock as out of stock)
          priceOverride:
            variantData.priceOverride || variantData.price || data.price || 0,
          sku: variantData.sku || `${doc.id}_${size}_${color}`,
          isActive: variantData.isActive !== false,
          image: variantData.image || "",
          soldQuantity: variantData.soldQuantity || 0,
          barcode: variantData.barcode || null,
        };

        variantsArray.push(transformedVariant);
      }

      return variantsArray;
    } else {
      // Fallback for null/undefined/other types
      return [];
    }
  };

  // Extract sizes and colors from variants as fallback (if not in productAttributes)
  const extractSizesAndColorsFromVariants = (variants: any) => {
    const sizes = new Set<string>();
    const colors = new Set<string>();

    if (Array.isArray(variants)) {
      variants.forEach((v: any) => {
        const size =
          v?.attributeValues?.Size ??
          v?.attributeValues?.size ??
          v?.size ??
          v?.Size;
        const color =
          v?.attributeValues?.Color ??
          v?.attributeValues?.color ??
          v?.color ??
          v?.Color;

        if (typeof size === "string" && size.trim() !== "") {
          sizes.add(size);
        }
        if (typeof color === "string" && color.trim() !== "") {
          colors.add(color);
        }
      });
    } else if (variants && typeof variants === "object") {
      Object.keys(variants).forEach((key) => {
        const keyParts = key.split("_");
        const size = keyParts[0] || "";
        const color = keyParts.slice(1).join("_") || "";

        if (size.trim() !== "") {
          sizes.add(size);
        }
        if (color.trim() !== "") {
          colors.add(color);
        }
      });
    }

    return {
      sizes: Array.from(sizes),
      colors: Array.from(colors),
    };
  };

  const { sizes: variantSizes, colors: variantColors } =
    extractSizesAndColorsFromVariants(data.variants);

  // Use productAttributes as primary source, fallback to variants, then to direct fields
  const finalSizes =
    attributeSizes.length > 0
      ? attributeSizes
      : variantSizes.length > 0
      ? variantSizes
      : data.sizes || [];

  const finalColors =
    attributeColors.length > 0
      ? attributeColors
      : variantColors.length > 0
      ? variantColors
      : data.colors || [];

  // Extract images from multiple possible fields
  const extractImages = (): string[] => {
    // Check all possible image fields in order of preference
    const candidates: string[] = [];
    
    // Primary: images array
    if (Array.isArray(data.images)) {
      candidates.push(...data.images);
    }
    
    // Fallback: single image fields
    if (data.thumbnail && typeof data.thumbnail === 'string' && data.thumbnail.trim()) {
      candidates.push(data.thumbnail);
    }
    if (data.image && typeof data.image === 'string' && data.image.trim()) {
      candidates.push(data.image);
    }
    if (data.mainImage && typeof data.mainImage === 'string' && data.mainImage.trim()) {
      candidates.push(data.mainImage);
    }
    if (data.imageUrl && typeof data.imageUrl === 'string' && data.imageUrl.trim()) {
      candidates.push(data.imageUrl);
    }
    if (data.photo && typeof data.photo === 'string' && data.photo.trim()) {
      candidates.push(data.photo);
    }
    if (data.picture && typeof data.picture === 'string' && data.picture.trim()) {
      candidates.push(data.picture);
    }
    if (data.img && typeof data.img === 'string' && data.img.trim()) {
      candidates.push(data.img);
    }
    if (data.productImage && typeof data.productImage === 'string' && data.productImage.trim()) {
      candidates.push(data.productImage);
    }
    if (data.product_image && typeof data.product_image === 'string' && data.product_image.trim()) {
      candidates.push(data.product_image);
    }
    
    // Filter out invalid/placeholder images
    return candidates.filter(
      (url) =>
        typeof url === 'string' &&
        url.trim() !== '' &&
        !url.includes('placeholder') &&
        !url.includes('via.placeholder') &&
        (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/'))
    );
  };

  const extractedImages = extractImages();

  return {
    id: doc.id,
    title: data.title || "",
    description: data.description || "",
    thumbnail: data.thumbnail || "",
    images: extractedImages.length > 0 ? extractedImages : (data.images || []),
    price: data.price || 0,
    discountPrice: data.discountPrice || null,
    productType: data.productType || "simple",
    variants: transformVariants(data.variants),
    productAttributes: data.productAttributes || [],
    totalStock: data.totalStock ?? 0, // Default to 0 if not set (treat missing stock as out of stock)
    stockQty: data.stockQty ?? 0, // Default to 0 if not set (treat missing stock as out of stock)
    soldQuantity: data.soldQuantity || 0,
    rating: data.rating || 0,
    totalReviews: data.totalReviews || 0,
    isFeatured: data.isFeatured || false,
    isFlashSale: data.isFlashSale || false,
    hasFreeShipping: data.hasFreeShipping || false,
    status: data.status || "pending",
    sellerId: data.sellerId || "",
    shopName: data.shopName || "",
    brand: data.brand || "",
    sizes: finalSizes,
    colors: finalColors,
    tags: data.tags || [],
    categories: data.categories || [],
    categoryIds: data.categoryIds || [],
    weight: data.weight,
    weightUnit: data.weightUnit,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  } as Product;
};

// Helper function to map Firestore document to ProductCategory
const mapDocumentToProductCategory = (doc: any): ProductCategory => {
  const data = doc.data();
  return {
    id: doc.id,
    productId: data.productId || "",
    categoryId: data.categoryId || "",
    isPrimary: data.isPrimary || false,
    sortOrder: data.sortOrder || 0,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  } as ProductCategory;
};


// Helper function to map Firestore document to new Category interface
const mapDocumentToNewCategory = (doc: any): Category => {
  const data = doc.data();
  return {
    id: doc.id,
    name: data.name || data.Name || "",
    isActive:
      data.isActive || data.isActive === undefined ? true : data.isActive, // Default to true if not specified
    order: typeof data.order === "number" ? data.order : 999, // Validate order as number, fallback to 999
    parentId: data.parentId || data.parent_id || "",
    // Enhanced metadata for better UI support
    icon: data.icon || data.Icon || getDefaultCategoryIcon(data.name || ""),
    color: data.color || data.Color || getDefaultCategoryColor(data.name || ""),
    description: data.description || data.Description || "",
    image: data.image || data.Image || "",
    imageUrl: data.imageUrl || data.image_url || data.ImageUrl || "",
    thumbnailUrl:
      data.thumbnailUrl || data.thumbnail_url || data.ThumbnailUrl || "",
    slug: data.slug || data.Slug || generateSlug(data.name || ""),
    metaTitle: data.metaTitle || data.meta_title || data.name || "",
    metaDescription: data.metaDescription || data.meta_description || "",
    createdAt: data.createdAt || data.created_at || serverTimestamp(),
    updatedAt: data.updatedAt || data.updated_at || serverTimestamp(),
  } as Category;
};

// Helper function to get default category icon based on name
const getDefaultCategoryIcon = (categoryName: string): string => {
  const name = categoryName.toLowerCase();
  if (name.includes("men") || name.includes("male")) return "👔";
  if (name.includes("women") || name.includes("female")) return "👗";
  if (name.includes("kids") || name.includes("children")) return "👶";
  if (name.includes("accessories")) return "👜";
  if (name.includes("footwear") || name.includes("shoes")) return "👟";
  if (name.includes("electronics")) return "📱";
  if (name.includes("unisex")) return "👕";
  if (name.includes("gloves") || name.includes("scarves")) return "🧤";
  if (name.includes("hats") || name.includes("caps")) return "🧢";
  if (name.includes("umbrellas") || name.includes("rainwear")) return "☔";
  if (name.includes("clothing") || name.includes("hoodies")) return "👕";
  if (name.includes("traditional") || name.includes("khadaun")) return "🥿";
  return "📦"; // Default icon
};

// Helper function to get default category color based on name
const getDefaultCategoryColor = (categoryName: string): string => {
  const name = categoryName.toLowerCase();
  if (name.includes("men")) return "#3B82F6"; // Blue
  if (name.includes("women")) return "#EC4899"; // Pink
  if (name.includes("kids")) return "#10B981"; // Green
  if (name.includes("accessories")) return "#8B5CF6"; // Purple
  if (name.includes("footwear")) return "#F97316"; // Orange
  if (name.includes("electronics")) return "#6366F1"; // Indigo
  if (name.includes("gloves") || name.includes("scarves")) return "#da2516"; // Primary red
  if (name.includes("hats") || name.includes("caps")) return "#EAB308"; // Yellow
  if (name.includes("umbrellas") || name.includes("rainwear")) return "#3B82F6"; // Blue
  if (name.includes("traditional") || name.includes("khadaun"))
    return "#059669"; // Emerald
  return "#6B7280"; // Gray default
};

// Helper function to generate slug from category name
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single
    .trim();
};

// ProductCategory services
export const productCategoryService = {
  // Get all product-category relationships
  async getAllProductCategories(): Promise<ProductCategory[]> {
    try {
      const querySnapshot = await getDocs(productCategoryCollection);
      const productCategories = querySnapshot.docs.map(
        mapDocumentToProductCategory
      );
      return productCategories;
    } catch (error) {
      throw error;
    }
  },

  // Get categories for a specific product
  async getCategoriesForProduct(productId: string): Promise<ProductCategory[]> {
    try {
      const q = query(
        productCategoryCollection,
        where("productId", "==", productId)
      );
      const querySnapshot = await getDocs(q);
      const productCategories = querySnapshot.docs.map(
        mapDocumentToProductCategory
      );
      return productCategories.sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
      );
    } catch (error) {
      throw error;
    }
  },

  // Get products for a specific category
  async getProductsForCategory(categoryId: string): Promise<ProductCategory[]> {
    try {
      const q = query(
        productCategoryCollection,
        where("categoryId", "==", categoryId)
      );
      const querySnapshot = await getDocs(q);
      const productCategories = querySnapshot.docs.map(
        mapDocumentToProductCategory
      );
      return productCategories.sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
      );
    } catch (error) {
      throw error;
    }
  },

  // Get primary category for a product
  async getPrimaryCategoryForProduct(
    productId: string
  ): Promise<ProductCategory | null> {
    try {
      const q = query(
        productCategoryCollection,
        where("productId", "==", productId),
        where("isPrimary", "==", true)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        return mapDocumentToProductCategory(querySnapshot.docs[0]);
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Create product-category relationship
  // SECURITY: Admin only or product owner
  async createProductCategory(
    productCategoryData: Omit<ProductCategory, "id" | "createdAt" | "updatedAt">
  ): Promise<string> {
    try {
      // SECURITY: Validate inputs
      if (!productCategoryData.productId || !isValidDocumentId(productCategoryData.productId)) {
        throw new Error('Invalid product ID format');
      }
      if (!productCategoryData.categoryId || !isValidDocumentId(productCategoryData.categoryId)) {
        throw new Error('Invalid category ID format');
      }

      // SECURITY: Check admin role or product ownership
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to create category relationships');
      }

      // Check if user is admin
      let isAdmin = false;
      try {
        const userDoc = await getDoc(doc(db, 'Users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          isAdmin = userData?.role === 'admin';
        }
      } catch (error) {
        // If admin check fails, continue with ownership check
      }

      // If not admin, verify product ownership
      if (!isAdmin) {
        const productRef = doc(productsCollection, productCategoryData.productId);
        const productDoc = await getDoc(productRef);
        if (!productDoc.exists()) {
          throw new Error('Product not found');
        }
        const productData = productDoc.data();
        if (productData.sellerId !== currentUser.uid) {
          secureLog('warn', 'IDOR attempt detected - unauthorized category relationship creation', {
            productId: productCategoryData.productId,
            requestedSellerId: productData.sellerId,
            currentUserId: currentUser.uid,
            action: 'createProductCategory'
          });
          throw new Error('You can only create category relationships for your own products');
        }
      }

      const docRef = await addDoc(productCategoryCollection, {
        ...productCategoryData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      secureLog('error', 'Failed to create product category relationship', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Update product-category relationship
  // SECURITY: Admin only or product owner
  async updateProductCategory(
    productCategoryId: string,
    productCategoryData: Partial<ProductCategory>
  ): Promise<void> {
    try {
      // SECURITY: Validate inputs
      if (!productCategoryId || !isValidDocumentId(productCategoryId)) {
        throw new Error('Invalid product category ID format');
      }

      // SECURITY: Check admin role or product ownership
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to update category relationships');
      }

      // Get existing relationship to check product ownership
      const categoryRef = doc(productCategoryCollection, productCategoryId);
      const categoryDoc = await getDoc(categoryRef);
      if (!categoryDoc.exists()) {
        throw new Error('Product category relationship not found');
      }

      const existingData = categoryDoc.data();
      const productId = productCategoryData.productId || existingData.productId;

      // Check if user is admin
      let isAdmin = false;
      try {
        const userDoc = await getDoc(doc(db, 'Users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          isAdmin = userData?.role === 'admin';
        }
      } catch (error) {
        // If admin check fails, continue with ownership check
      }

      // If not admin, verify product ownership
      if (!isAdmin) {
        const productRef = doc(productsCollection, productId);
        const productDoc = await getDoc(productRef);
        if (!productDoc.exists()) {
          throw new Error('Product not found');
        }
        const productData = productDoc.data();
        if (productData.sellerId !== currentUser.uid) {
          secureLog('warn', 'IDOR attempt detected - unauthorized category relationship update', {
            productCategoryId,
            productId,
            requestedSellerId: productData.sellerId,
            currentUserId: currentUser.uid,
            action: 'updateProductCategory'
          });
          throw new Error('You can only update category relationships for your own products');
        }
      }

      await updateDoc(categoryRef, {
        ...productCategoryData,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      secureLog('error', 'Failed to update product category relationship', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Delete product-category relationship
  // SECURITY: Admin only or product owner
  async deleteProductCategory(productCategoryId: string): Promise<void> {
    try {
      // SECURITY: Validate inputs
      if (!productCategoryId || !isValidDocumentId(productCategoryId)) {
        throw new Error('Invalid product category ID format');
      }

      // SECURITY: Check admin role or product ownership
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to delete category relationships');
      }

      // Get existing relationship to check product ownership
      const categoryRef = doc(productCategoryCollection, productCategoryId);
      const categoryDoc = await getDoc(categoryRef);
      if (!categoryDoc.exists()) {
        throw new Error('Product category relationship not found');
      }

      const existingData = categoryDoc.data();
      const productId = existingData.productId;

      // Check if user is admin
      let isAdmin = false;
      try {
        const userDoc = await getDoc(doc(db, 'Users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          isAdmin = userData?.role === 'admin';
        }
      } catch (error) {
        // If admin check fails, continue with ownership check
      }

      // If not admin, verify product ownership
      if (!isAdmin) {
        const productRef = doc(productsCollection, productId);
        const productDoc = await getDoc(productRef);
        if (!productDoc.exists()) {
          throw new Error('Product not found');
        }
        const productData = productDoc.data();
        if (productData.sellerId !== currentUser.uid) {
          secureLog('warn', 'IDOR attempt detected - unauthorized category relationship deletion', {
            productCategoryId,
            productId,
            requestedSellerId: productData.sellerId,
            currentUserId: currentUser.uid,
            action: 'deleteProductCategory'
          });
          throw new Error('You can only delete category relationships for your own products');
        }
      }

      await deleteDoc(categoryRef);
    } catch (error) {
      secureLog('error', 'Failed to delete product category relationship', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Delete all relationships for a product
  // SECURITY: Admin only or product owner
  async deleteAllCategoriesForProduct(productId: string): Promise<void> {
    try {
      // SECURITY: Validate inputs
      if (!productId || !isValidDocumentId(productId)) {
        throw new Error('Invalid product ID format');
      }

      // SECURITY: Check admin role or product ownership
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to delete category relationships');
      }

      // Check if user is admin
      let isAdmin = false;
      try {
        const userDoc = await getDoc(doc(db, 'Users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          isAdmin = userData?.role === 'admin';
        }
      } catch (error) {
        // If admin check fails, continue with ownership check
      }

      // If not admin, verify product ownership
      if (!isAdmin) {
        const productRef = doc(productsCollection, productId);
        const productDoc = await getDoc(productRef);
        if (!productDoc.exists()) {
          throw new Error('Product not found');
        }
        const productData = productDoc.data();
        if (productData.sellerId !== currentUser.uid) {
          secureLog('warn', 'IDOR attempt detected - unauthorized category deletion', {
            productId,
            requestedSellerId: productData.sellerId,
            currentUserId: currentUser.uid,
            action: 'deleteAllCategoriesForProduct'
          });
          throw new Error('You can only delete category relationships for your own products');
        }
      }

      const q = query(
        productCategoryCollection,
        where("productId", "==", productId)
      );
      const querySnapshot = await getDocs(q);
      const deletePromises = querySnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);
    } catch (error) {
      secureLog('error', 'Failed to delete all categories for product', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Delete all relationships for a category
  // SECURITY: Admin only
  async deleteAllProductsForCategory(categoryId: string): Promise<void> {
    try {
      // SECURITY: Validate inputs
      if (!categoryId || !isValidDocumentId(categoryId)) {
        throw new Error('Invalid category ID format');
      }

      // SECURITY: Check admin role
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to delete category relationships');
      }

      const userDoc = await getDoc(doc(db, 'Users', currentUser.uid));
      const userData = userDoc.data();
      if (userData?.role !== 'admin') {
        secureLog('warn', 'IDOR attempt detected - unauthorized category relationship deletion', {
          categoryId,
          currentUserId: currentUser.uid,
          action: 'deleteAllProductsForCategory'
        });
        throw new Error('Unauthorized - Admin access required');
      }

      const q = query(
        productCategoryCollection,
        where("categoryId", "==", categoryId)
      );
      const querySnapshot = await getDocs(q);
      const deletePromises = querySnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);
    } catch (error) {
      secureLog('error', 'Failed to delete all products for category', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },
};

// Category services - corrected according to FIRESTORE_CONNECTIONS.md
export const categoryService = {
  // Get all categories
  async getAllCategories(limitCount: number = 1000): Promise<Category[]> {
    try {
      const q = query(categoriesCollection, limit(Math.min(limitCount, 5000))); // Cap at 5000 for safety
      const querySnapshot = await getDocs(q);
      const categories = querySnapshot.docs.map(mapDocumentToNewCategory);
      return categories;
    } catch (error) {
      throw error;
    }
  },

  // Get featured categories
  async getFeaturedCategories(): Promise<Category[]> {
    try {
      const q = query(categoriesCollection, where("isFeatured", "==", true), limit(100));
      const querySnapshot = await getDocs(q);
      const categories = querySnapshot.docs.map(mapDocumentToNewCategory);
      return categories;
    } catch (error) {
      throw error;
    }
  },


  // Create new category (admin only)
  async createCategory(
    categoryData: Omit<Category, "id" | "createdAt" | "updatedAt">
  ): Promise<string> {
    try {
      // Security: Check admin role
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User must be authenticated to perform this action");
      }
      
      const userDoc = await getDoc(doc(db, 'Users', user.uid));
      const userData = userDoc.data();
      if (userData?.role !== 'admin') {
        throw new Error("Unauthorized - Admin access required");
      }

      const docRef = await addDoc(categoriesCollection, {
        ...categoryData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Update category (admin only)
  async updateCategory(
    categoryId: string,
    categoryData: Partial<Category>
  ): Promise<void> {
    try {
      // Security: Check admin role
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User must be authenticated to perform this action");
      }
      
      const userDoc = await getDoc(doc(db, 'Users', user.uid));
      const userData = userDoc.data();
      if (userData?.role !== 'admin') {
        throw new Error("Unauthorized - Admin access required");
      }

      const docRef = doc(categoriesCollection, categoryId);
      await updateDoc(docRef, {
        ...categoryData,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  },

  // Delete category (admin only)
  async deleteCategory(categoryId: string): Promise<void> {
    try {
      // Security: Check admin role
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User must be authenticated to perform this action");
      }
      
      const userDoc = await getDoc(doc(db, 'Users', user.uid));
      const userData = userDoc.data();
      if (userData?.role !== 'admin') {
        throw new Error("Unauthorized - Admin access required");
      }

      const docRef = doc(categoriesCollection, categoryId);
      await deleteDoc(docRef);
    } catch (error) {
      throw error;
    }
  },

  // New methods using the updated Category interface
  // Get categories by parent ID with proper filtering and ordering
  // QA: useSubcategories("kids_unisex") should return ["kids_unisex_clothing","kids_unisex_shoes","kids_unisex_accessories"] sorted by "order".
  async getCategoriesByParent(
    parentId: string,
    limitCount?: number
  ): Promise<Category[]> {
    try {
      // SECURITY: Validate parentId format to prevent injection attacks
      if (!parentId || typeof parentId !== 'string') {
        secureLog('warn', 'Invalid parentId format in getCategoriesByParent');
        return [];
      }
      
      // SECURITY: Validate parentId length and format
      if (parentId.length === 0 || parentId.length > 1500) {
        secureLog('warn', 'Invalid parentId length in getCategoriesByParent');
        return [];
      }
      
      // SECURITY: Prevent path traversal and injection
      if (parentId.includes('..') || parentId.includes('/') || parentId.includes('\\')) {
        secureLog('warn', 'Invalid parentId format (path traversal attempt) in getCategoriesByParent');
        return [];
      }
      
      // SECURITY: Validate parentId format (alphanumeric with underscores and hyphens)
      if (!/^[a-zA-Z0-9_-]+$/.test(parentId)) {
        secureLog('warn', 'Invalid parentId format (invalid characters) in getCategoriesByParent');
        return [];
      }
      
      // Removed verbose logging - category queries happen too frequently
      let q = query(
        categoriesCollection,
        where("parentId", "==", parentId),
        where("isActive", "==", true),
        orderBy("order", "asc")
      );

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);

      const categories = querySnapshot.docs.map(mapDocumentToNewCategory);

      // Additional client-side sorting for deterministic results (tiebreaker by id)
      const sortedCategories = categories.sort((a, b) => {
        if (a.order !== b.order) {
          return a.order - b.order;
        }
        return a.id.localeCompare(b.id); // Secondary sort by id for deterministic results
      });

      return sortedCategories;
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
        secureLog('warn', 'Permission denied for categories by parent');
        return [];
      }
      secureLog('warn', 'Failed to get categories by parent');
      throw error;
    }
  },

  // Get top-level categories (alias for getCategoriesByParent with "fashion" parent)
  async getTopLevelCategories(): Promise<Category[]> {
    try {
      return await this.getCategoriesByParent("fashion");
    } catch (error: any) {
      // Handle permission errors gracefully
      if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
        secureLog('warn', 'Permission denied for top level categories');
        return [];
      }
      secureLog('error', 'Failed to get top level categories', { error: error?.message });
      throw error;
    }
  },

  // Get category by ID using new interface
  async getCategoryByIdNew(id: string): Promise<Category | null> {
    try {
      // SECURITY: Validate category ID format to prevent injection attacks
      if (!id || typeof id !== 'string') {
        secureLog('warn', 'Invalid category ID format in getCategoryByIdNew');
        return null;
      }
      
      // SECURITY: Validate category ID length and format
      if (id.length === 0 || id.length > 1500) {
        secureLog('warn', 'Invalid category ID length in getCategoryByIdNew');
        return null;
      }
      
      // SECURITY: Prevent path traversal and injection
      if (id.includes('..') || id.includes('/') || id.includes('\\')) {
        secureLog('warn', 'Invalid category ID format (path traversal attempt) in getCategoryByIdNew');
        return null;
      }
      
      // SECURITY: Validate category ID format (alphanumeric with underscores and hyphens)
      if (!/^[a-zA-Z0-9_-]+$/.test(id)) {
        secureLog('warn', 'Invalid category ID format (invalid characters) in getCategoryByIdNew');
        return null;
      }
      
      const docRef = doc(categoriesCollection, id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const category = mapDocumentToNewCategory(docSnap);
        // Only return if category is active
        return category.isActive ? category : null;
      }
      return null;
    } catch (error) {
      throw error;
    }
  },

  // Get category with product count
  async getCategoryWithProductCount(
    id: string
  ): Promise<(Category & { productCount: number }) | null> {
    try {
      const category = await this.getCategoryByIdNew(id);
      if (!category) return null;

      // Get product count for this category
      const productCount = await this.getProductCountForCategory(id);

      return {
        ...category,
        productCount,
      };
    } catch (error) {
      throw error;
    }
  },

  // Get product count for a category
  async getProductCountForCategory(categoryId: string): Promise<number> {
    try {
      // SECURITY: Validate category ID format to prevent injection attacks
      if (!categoryId || typeof categoryId !== 'string') {
        secureLog('warn', 'Invalid category ID format in getProductCountForCategory');
        return 0;
      }
      
      // SECURITY: Validate category ID length and format
      if (categoryId.length === 0 || categoryId.length > 1500) {
        secureLog('warn', 'Invalid category ID length in getProductCountForCategory');
        return 0;
      }
      
      // SECURITY: Prevent path traversal and injection
      if (categoryId.includes('..') || categoryId.includes('/') || categoryId.includes('\\')) {
        secureLog('warn', 'Invalid category ID format (path traversal attempt) in getProductCountForCategory');
        return 0;
      }
      
      // SECURITY: Validate category ID format (alphanumeric with underscores and hyphens)
      if (!/^[a-zA-Z0-9_-]+$/.test(categoryId)) {
        secureLog('warn', 'Invalid category ID format (invalid characters) in getProductCountForCategory');
        return 0;
      }
      
      const q = query(
        productsCollection,
        where("categoryIds", "array-contains", categoryId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      secureLog('warn', 'Failed to get product count for category');
      return 0;
    }
  },

  // Get categories with product counts
  async getCategoriesWithProductCounts(
    parentId?: string
  ): Promise<(Category & { productCount: number })[]> {
    try {
      const categories = parentId
        ? await this.getCategoriesByParent(parentId)
        : await this.getTopLevelCategories();

      // Get product counts for all categories
      const categoriesWithCounts = await Promise.all(
        categories.map(async (category) => {
          const productCount = await this.getProductCountForCategory(
            category.id
          );
          return {
            ...category,
            productCount,
          };
        })
      );

      return categoriesWithCounts;
    } catch (error) {
      throw error;
    }
  },

  // Get category hierarchy (category + all children)
  async getCategoryHierarchy(categoryId: string): Promise<{
    category: Category;
    children: Category[];
    productCount: number;
  } | null> {
    try {
      const category = await this.getCategoryByIdNew(categoryId);
      if (!category) return null;

      const children = await this.getCategoriesByParent(categoryId);
      const productCount = await this.getProductCountForCategory(categoryId);

      return {
        category,
        children,
        productCount,
      };
    } catch (error) {
      throw error;
    }
  },

  // Search categories by name
  async searchCategories(
    searchTerm: string,
    parentId?: string
  ): Promise<Category[]> {
    try {
      const categories = parentId
        ? await this.getCategoriesByParent(parentId)
        : await this.getTopLevelCategories();

      const searchLower = searchTerm.toLowerCase();
      return categories.filter(
        (category) =>
          category.name.toLowerCase().includes(searchLower) ||
          category.description?.toLowerCase().includes(searchLower)
      );
    } catch (error) {
      throw error;
    }
  },

  // Get breadcrumb trail for a category
  async getBreadcrumbTrail(categoryId: string): Promise<Category[]> {
    try {
      const breadcrumb: Category[] = [];
      let currentCategory = await this.getCategoryByIdNew(categoryId);

      while (currentCategory) {
        breadcrumb.unshift(currentCategory);

        if (
          currentCategory.parentId &&
          currentCategory.parentId !== "fashion"
        ) {
          currentCategory = await this.getCategoryByIdNew(
            currentCategory.parentId
          );
        } else {
          break;
        }
      }

      return breadcrumb;
    } catch (error) {
      secureLog('warn', 'Failed to get breadcrumb trail');
      return [];
    }
  },

  // Get root categories (categories where parentId == "" and isActive == true, ordered by order ASC)
  async getRootCategories(): Promise<Category[]> {
    return await this.getCategoriesByParent("");
  },

  // Get subcategories (alias for getCategoriesByParent - same functionality)
  async getSubcategories(parentId: string): Promise<Category[]> {
    return await this.getCategoriesByParent(parentId);
  },

  // Get all category IDs for a given category and its descendants (batched fetching)
  async getAllCategoryIds(categoryId: string): Promise<string[]> {
    try {
      // SECURITY: Validate category ID format
      if (!categoryId || typeof categoryId !== 'string') {
        secureLog('warn', 'Invalid category ID format in getAllCategoryIds');
        return [];
      }

      if (categoryId.length === 0 || categoryId.length > 1500) {
        secureLog('warn', 'Invalid category ID length in getAllCategoryIds');
        return [];
      }

      if (categoryId.includes('..') || categoryId.includes('/') || categoryId.includes('\\')) {
        secureLog('warn', 'Invalid category ID format (path traversal attempt) in getAllCategoryIds');
        return [];
      }

      if (!/^[a-zA-Z0-9_-]+$/.test(categoryId)) {
        secureLog('warn', 'Invalid category ID format (invalid characters) in getAllCategoryIds');
        return [];
      }

      const allIds = [categoryId];
      const queue = [categoryId];

      console.log(`[CategoryService] Resolving hierarchical categories for: "${categoryId}"`);

      // Breadth-first traversal to get all descendants
      while (queue.length > 0) {
        const currentIds = [...queue];
        queue.length = 0; // Clear queue

        // Batch fetch children for all current level categories
        const childPromises = currentIds.map(id => this.getCategoriesByParent(id));
        const childResults = await Promise.all(childPromises);

        // Collect all children and add to queue for next level
        childResults.forEach((children, index) => {
          if (children.length > 0) {
            console.log(`[CategoryService] Found ${children.length} children for "${currentIds[index]}":`, children.map(c => c.id));
          }
          children.forEach(child => {
            allIds.push(child.id);
            queue.push(child.id);
          });
        });
      }

      console.log(`[CategoryService] Total categories resolved for "${categoryId}": ${allIds.length} categories`, allIds);
      return allIds;
    } catch (error: any) {
      secureLog('warn', 'Failed to get all category IDs', { error: error?.message });
      // Return just the original category ID as fallback
      return [categoryId];
    }
  },
};

// Helper function to check if a product should be visible (approved or no status for backward compatibility)
const isProductVisible = (product: Product | any): boolean => {
  // Allow products without status field (backward compatibility) or with approved status
  // Reject products with explicit rejected/pending status
  const status = product?.status;
  if (status === undefined || status === null || status === 'approved') {
    return true;
  }
  return false;
};

// Product services - corrected according to FIRESTORE_CONNECTIONS.md
export const productService = {
  // Get all products - only returns approved products or products without status
  // PERFORMANCE OPTIMIZATION: Use where() query to filter server-side instead of fetching all
  async getAllProducts(): Promise<Product[]> {
    try {
  // PERFORMANCE: Use where() query to only fetch approved products (server-side filtering)
      // This is much faster than fetching all products and filtering client-side
      // Similar to cart service's "only fetch selected items" optimization
      const approvedQuery = query(
        productsCollection,
        where("status", "==", "approved"),
        limit(5000)
      );
      const approvedSnapshot = await getDocs(approvedQuery);
      const approvedProducts = approvedSnapshot.docs.map(productFromSnap);
      
      // PERFORMANCE: Early exit if we have enough approved products
      // Only fetch products without status if we need to (for backward compatibility)
      if (approvedProducts.length > 0) {
        return approvedProducts;
      }
      
      // Fallback: Fetch products without status (legacy products)
      // This is only executed if no approved products exist
      const allQuery = query(productsCollection, limit(5000));
      const allSnapshot = await getDocs(allQuery);
      const allProducts = allSnapshot.docs.map(productFromSnap);
      
      // Filter to only return visible products (approved or no status)
      return allProducts.filter(isProductVisible);
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
        secureLog('warn', 'Permission denied for all products');
        return [];
      }
      throw error;
    }
  },

  // Get paginated products with filters (optimized for performance)
  // PERFORMANCE OPTIMIZATION: Uses where() queries for server-side filtering (like cart service)
  async getPaginatedProducts({
    pageSize = 20,
    cursor,
    categoryId,
    sellerId,
    searchQuery,
    sortBy = "updatedAt",
    sortOrder = "desc",
    minPrice,
    maxPrice,
  }: {
    pageSize?: number;
    cursor?: any;
    categoryId?: string;
    sellerId?: string;
    searchQuery?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    minPrice?: number;
    maxPrice?: number;
  } = {}): Promise<{ items: Product[]; nextCursor: any; hasMore: boolean }> {
    try {
      // PERFORMANCE: Early exit for invalid page size
      if (pageSize <= 0 || pageSize > 100) {
        return { items: [], nextCursor: null, hasMore: false };
      }

      // Check if we have price filters
      const hasPriceFilters = minPrice !== undefined || maxPrice !== undefined;

      let q;

      // PERFORMANCE: If no price filters, use simpler query (no complex index needed)
      // This is faster and doesn't require composite indexes
      if (!hasPriceFilters) {
        // PERFORMANCE: Always filter by status server-side (like cart service's where() optimization)
        q = query(productsCollection, where("status", "==", "approved"));

        // Add seller filter if provided (server-side filtering)
        if (sellerId) {
          q = query(q, where("sellerId", "==", sellerId));
        }

        // Add category filter if provided
        if (categoryId) {
          q = query(q, where("categoryIds", "array-contains", categoryId));
        }

        // Add sorting
        q = query(q, orderBy(sortBy, sortOrder));

        // Add pagination
        q = query(q, limit(pageSize + 1));

        if (cursor) {
          q = query(q, startAfter(cursor));
        }
      } else {
        // With price filters - requires composite index
        q = query(productsCollection, where("status", "==", "approved"));

        // Add seller filter if provided (server-side filtering)
        if (sellerId) {
          q = query(q, where("sellerId", "==", sellerId));
        }

        // Add category filter if provided
        if (categoryId) {
          q = query(q, where("categoryIds", "array-contains", categoryId));
        }

        // Add price range filters
        if (minPrice !== undefined) {
          q = query(q, where("price", ">=", minPrice));
        }
        if (maxPrice !== undefined) {
          q = query(q, where("price", "<=", maxPrice));
        }

        // Add sorting (must orderBy price first when using price filters)
        q = query(q, orderBy("price", sortOrder));
        if (sortBy !== "price") {
          q = query(q, orderBy(sortBy, sortOrder));
        }

        // Add pagination
        q = query(q, limit(pageSize + 1));

        if (cursor) {
          q = query(q, startAfter(cursor));
        }
      }

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;

      // PERFORMANCE: Early exit if no results
      if (docs.length === 0) {
        return { items: [], nextCursor: null, hasMore: false };
      }

      // Check if there are more items
      const hasMore = docs.length > pageSize;
      const items = docs.slice(0, pageSize).map(productFromSnap);

      // PERFORMANCE: Apply client-side search filter only if needed (Firestore doesn't support text search)
      // This is similar to cart service's "only process what's needed" pattern
      let filteredItems = items;
      if (searchQuery && searchQuery.trim().length > 0) {
        const lowerSearch = searchQuery.toLowerCase().trim();
        filteredItems = items.filter(
          (product) =>
            String(product.title || "").toLowerCase().includes(lowerSearch) ||
            String(product.description || "").toLowerCase().includes(lowerSearch) ||
            String(product.brand || "").toLowerCase().includes(lowerSearch)
        );
      }

      const nextCursor = hasMore ? docs[pageSize - 1] : null;

      return {
        items: filteredItems,
        nextCursor,
        hasMore,
      };
    } catch (error: any) {
      // Surface Firestore index creation link in console for quicker fixes
      const message: string = error?.message || '';
      if (message.toLowerCase().includes('index') || message.includes('create_composite')) {
        const linkMatch = message.match(/https:\/\/console\.firebase\.google\.com\/[^\s"]+/);
        const indexLink = linkMatch ? linkMatch[0] : null;
        if (indexLink) {
          // Safe to log: contains only the Firebase console index URL
          console.info('Firestore index required. Create it here:', indexLink);
          secureLog('warn', 'Firestore index required for paginated products', { indexLink });
          // Show a concise error to the UI; keep link in console
          throw new Error('This query needs a Firestore index. Check console for the creation link.');
        }
      }
      // Handle permission errors gracefully for non-authenticated users
      if (
        error?.code === "permission-denied" ||
        error?.message?.includes("permissions")
      ) {
        secureLog('warn', "Permission denied for paginated products - user not authenticated, returning empty array");
        return { items: [], nextCursor: null, hasMore: false };
      }
      throw error;
    }
  },

  // Get product by ID - only returns approved products or products without status
  // Performance: Uses Firestore cache-first pattern (already implemented in firestoreCache.ts)
  async getProductById(id: string): Promise<Product | null> {
    try {
      const docRef = doc(productsCollection, id);
      // Use cache-first helper from firestoreCache
      const { getDocCacheFirst } = await import('../lib/firestoreCache');
      const docSnap = await getDocCacheFirst(docRef);

      if (docSnap.exists()) {
        const product = productFromSnap(docSnap);
        // Only return product if it's visible (approved or no status)
        if (isProductVisible(product)) {
          return product;
        }
        // Product exists but is not approved/rejected - return null
        return null;
      }

      return null;
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
        secureLog('warn', 'Permission denied for product by ID');
        return null;
      }
      throw error;
    }
  },

  // Search products - only returns approved products or products without status
  // Simple search function (legacy - consider using superSearchProducts for better results)
  // Performance: Limited product fetching to prevent API blocking
  async searchProducts(searchTerm: string): Promise<Product[]> {
    const MAX_PRODUCTS_TO_FETCH = 500; // Limit to prevent API blocking
    
    try {
      // Query for approved products first (server-side filtering) with LIMIT
      const approvedQuery = query(
        productsCollection,
        where("status", "==", "approved"),
        limit(MAX_PRODUCTS_TO_FETCH) // CRITICAL: Limit to prevent API blocking
      );
      const approvedSnapshot = await getDocs(approvedQuery);
      const approvedProducts = approvedSnapshot.docs.map(productFromSnap);

      // Also search in products without status (for backward compatibility) with LIMIT
      const allQuery = query(
        productsCollection,
        limit(MAX_PRODUCTS_TO_FETCH) // CRITICAL: Limit to prevent API blocking
      );
      const allSnapshot = await getDocs(allQuery);
      const allProducts = allSnapshot.docs.map(productFromSnap);
      
      const approvedIds = new Set(approvedProducts.map(p => p.id));
      const productsWithoutStatus = allProducts.filter(
        (p) => !approvedIds.has(p.id) && (p.status === undefined || p.status === null)
      );

      // Combine and filter by visibility
      const allVisibleProducts = [...approvedProducts, ...productsWithoutStatus].filter(isProductVisible);

      // Apply search filter
      const lowerSearch = searchTerm.toLowerCase();
      return allVisibleProducts.filter(
        (product: Product) =>
          product.title?.toLowerCase().includes(lowerSearch) ||
          product.description?.toLowerCase().includes(lowerSearch) ||
          product.brand?.toLowerCase().includes(lowerSearch)
      );
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
        secureLog('warn', 'Permission denied for search products');
        return [];
      }
      throw error;
    }
  },

  // Enhanced intelligent search with synonyms, multi-field search, and relevance ranking
  // Security: Only returns publicly visible products (approved or no status)
  // Performance: Limited product fetching, rate limiting, and timeout protection to prevent API blocking
  async superSearchProducts(rawQuery: string): Promise<Product[]> {
    // Timeout protection: 15 seconds max for search operation
    const SEARCH_TIMEOUT_MS = 15000;
    const MAX_PRODUCTS_TO_FETCH = 500; // Limit to prevent API blocking
    const MAX_RESULTS_TO_RETURN = 100; // Limit final results

    // Rate limiting to prevent API abuse
    const rateLimitKey = 'super_search_products';
    const rateLimitResult = firebaseRateLimiter.isAllowed(rateLimitKey);
    if (!rateLimitResult.allowed) {
      secureLog('warn', 'Search rate limit exceeded', { queryLength: rawQuery?.length || 0 });
      return [];
    }

    const searchPromise = (async () => {
      // Check Firebase configuration immediately (silent check, no logging)
      if ((window as any).__FIREBASE_CONFIG_ERROR__) {
        secureLog('error', 'Firebase not configured for search');
        return [];
      }
      
      // Check if using placeholder config by checking the app instance
      try {
        const currentApp = getApp();
        const projectId = currentApp.options.projectId;
        if (projectId === 'placeholder-project' || !projectId) {
          secureLog('error', 'Firebase using placeholder config');
          return [];
        }
      } catch (err) {
        secureLog('error', 'Could not access Firebase app for search');
        return [];
      }
      
      try {
        // Basic validation - be lenient
        if (!rawQuery || typeof rawQuery !== 'string') {
          secureLog('warn', 'Invalid search query type');
          return [];
        }
        
        const trimmed = rawQuery.trim();
        if (trimmed.length < 2) {
          return [];
        }

        // SECURITY: Use comprehensive sanitization function instead of basic replacement
        // This prevents NoSQL injection, XSS, command injection, and other attacks
        let sanitizedQuery = sanitizeSearchQuery(trimmed, 100);
        
        // If sanitization fails, return empty results
        if (!sanitizedQuery || sanitizedQuery.length < 2) {
          secureLog('warn', 'Search query failed sanitization', { 
            queryLength: trimmed.length 
          });
          return [];
        }
        
        // SECURITY: Additional validation to ensure query is safe
        if (!isValidSearchQuery(sanitizedQuery)) {
          secureLog('warn', 'Search query failed validation', { 
            queryLength: sanitizedQuery.length 
          });
          return [];
        }

        // Expand query with synonyms
        const expandedTokens = expandSearchQuery(sanitizedQuery);
        
        // Always include the original query in tokens
        const allTokens = new Set<string>();
        allTokens.add(sanitizedQuery.toLowerCase());
        if (expandedTokens.length > 0) {
          expandedTokens.forEach(token => allTokens.add(token.toLowerCase()));
        }
        
        const finalTokens = Array.from(allTokens);
        if (finalTokens.length === 0) {
          return [];
        }

        // PERFORMANCE: Limit product fetching to prevent API blocking
        // Fetch visible products with a limit (approved or no status)
        // Security: Only fetch products that should be visible to buyers
        let allVisibleProducts: Product[] = [];
        
        try {
          // First, try to get approved products with LIMIT to prevent fetching all
          const approvedQuery = query(
            productsCollection,
            where("status", "==", "approved"),
            limit(MAX_PRODUCTS_TO_FETCH) // CRITICAL: Limit to prevent API blocking
          );
          const approvedSnapshot = await getDocs(approvedQuery);
          const approvedProducts = approvedSnapshot.docs.map(productFromSnap);
          allVisibleProducts = approvedProducts.filter(isProductVisible);
        } catch (error: any) {
          // If approved query fails, log but continue
          secureLog('error', 'Error fetching approved products in search', {
            code: error?.code,
            errorType: error?.name
          });
          if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
            secureLog('warn', 'Permission denied for approved products in search');
          }
          // Continue to try other methods
        }

        // If we have approved products, use them (same early exit pattern as getAllProducts)
        // Only fetch products without status if we have no approved products (for backward compatibility)
        if (allVisibleProducts.length === 0) {
          try {
            // CRITICAL: Add limit here too to prevent fetching all products
            const allQuery = query(
              productsCollection,
              limit(MAX_PRODUCTS_TO_FETCH) // Limit to prevent API blocking
            );
            const allSnapshot = await getDocs(allQuery);
            const allProducts = allSnapshot.docs.map(productFromSnap);
            
            // Filter to only return visible products (approved or no status)
            allVisibleProducts = allProducts.filter(isProductVisible);
          } catch (error: any) {
            // If this fails, continue with what we have
            secureLog('error', 'Error fetching all products in search', {
              code: error?.code,
              errorType: error?.name
            });
            if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
              secureLog('warn', 'Permission denied for all products in search');
            }
            // Continue to try fallback
          }
        }
        
        // If we still have no products, try using getAllProducts as a fallback
        // This uses the same method that works elsewhere in the app
        if (allVisibleProducts.length === 0) {
          // Try using getAllProducts as fallback (same method used elsewhere)
          // Note: getAllProducts doesn't have a limit, but we'll limit results after
          try {
            allVisibleProducts = await this.getAllProducts();
            // CRITICAL: Limit results even from getAllProducts to prevent too many products
            allVisibleProducts = allVisibleProducts.slice(0, MAX_PRODUCTS_TO_FETCH);
          } catch (fallbackError: any) {
            secureLog('error', 'All search methods failed', { 
              code: fallbackError?.code,
              errorType: fallbackError?.name
            });
          }
          
          // If still no products, return empty
          if (allVisibleProducts.length === 0) {
            return [];
          }
        }

      // Security: Create a safe product representation (only public fields)
      // This prevents IDOR by ensuring we don't expose internal fields
      const safeProducts = allVisibleProducts.map((product) => {
        // Only include public-facing fields
        return {
          id: product.id,
          title: product.title,
          description: product.description,
          thumbnail: product.thumbnail,
          images: product.images,
          price: product.price,
          discountPrice: product.discountPrice,
          productType: product.productType,
          variants: product.variants,
          productAttributes: product.productAttributes,
          totalStock: product.totalStock,
          stockQty: product.stockQty,
          soldQuantity: product.soldQuantity,
          rating: product.rating,
          totalReviews: product.totalReviews,
          isFeatured: product.isFeatured,
          isFlashSale: product.isFlashSale,
          status: product.status,
          sellerId: product.sellerId,
          shopName: product.shopName,
          brand: product.brand,
          sizes: product.sizes,
          colors: product.colors,
          tags: product.tags,
          categories: product.categories,
          categoryIds: product.categoryIds,
          weight: product.weight,
          weightUnit: product.weightUnit,
          material: product.material,
          createdAt: product.createdAt,
          updatedAt: product.updatedAt,
        } as Product;
      });

      // Search across multiple fields with expanded tokens
      const lowerQuery = sanitizedQuery.toLowerCase();
      const lowerTokens = finalTokens;

      // Score products based on relevance
      const scoredProducts = safeProducts.map((product) => {
        let score = 0;
        
        // Build searchable text from all relevant fields
        const title = (product.title || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        const brand = (product.brand || '').toLowerCase();
        const shopName = (product.shopName || '').toLowerCase();
        const tags = (product.tags || []).map((t: any) => 
          typeof t === 'string' ? t.toLowerCase() : ''
        ).join(' ');
        const categories = (product.categories || []).map((c: any) => 
          typeof c === 'string' ? c.toLowerCase() : ''
        ).join(' ');
        const material = (product.material || '').toLowerCase();
        
        // Combine all searchable text
        const productText = `${title} ${description} ${brand} ${shopName} ${tags} ${categories} ${material}`.toLowerCase();

        // Check each expanded token
        for (const token of lowerTokens) {
          let tokenLower = token.toLowerCase().trim();
          
          // Skip empty tokens
          if (!tokenLower || tokenLower.length < 1) continue;
          
          // Also try without hyphens for better matching (e.g., "t-shirt" -> "tshirt")
          const tokenWithoutHyphen = tokenLower.replace(/-/g, '');
          
          // Exact match in title (highest weight)
          const titleWithoutHyphen = title.replace(/-/g, '');
          if (title && (title.includes(tokenLower) || titleWithoutHyphen.includes(tokenWithoutHyphen))) {
            score += 10;
            // Exact title match gets bonus
            if (title === tokenLower || title.split(/\s+/).includes(tokenLower) || 
                titleWithoutHyphen === tokenWithoutHyphen || titleWithoutHyphen.includes(tokenWithoutHyphen)) {
              score += 5;
            }
            // Word boundary match (starts with or ends with)
            if (title.startsWith(tokenLower) || title.endsWith(tokenLower) ||
                titleWithoutHyphen.startsWith(tokenWithoutHyphen) || titleWithoutHyphen.endsWith(tokenWithoutHyphen)) {
              score += 3;
            }
          }
          
          // Match in brand (high weight) - also try without hyphens
          const brandWithoutHyphen = brand.replace(/-/g, '');
          if (brand && (brand.includes(tokenLower) || brandWithoutHyphen.includes(tokenWithoutHyphen))) {
            score += 8;
            if (brand === tokenLower || brandWithoutHyphen === tokenWithoutHyphen) {
              score += 4;
            }
          }
          
          // Match in tags string (high weight) - tags is already joined into a string
          const tagsWithoutHyphen = tags.replace(/-/g, '');
          if (tags && tags.length > 0 && (tags.includes(tokenLower) || tagsWithoutHyphen.includes(tokenWithoutHyphen))) {
            score += 7;
          }
          
          // Match in categories string (medium weight) - categories is already joined into a string
          const categoriesWithoutHyphen = categories.replace(/-/g, '');
          if (categories && categories.length > 0 && (categories.includes(tokenLower) || categoriesWithoutHyphen.includes(tokenWithoutHyphen))) {
            score += 6;
          }
          
          // Match in description (lower weight)
          const descriptionWithoutHyphen = description.replace(/-/g, '');
          if (description && (description.includes(tokenLower) || descriptionWithoutHyphen.includes(tokenWithoutHyphen))) {
            score += 3;
          }
          
          // Match in material (lower weight)
          const materialWithoutHyphen = material.replace(/-/g, '');
          if (material && (material.includes(tokenLower) || materialWithoutHyphen.includes(tokenWithoutHyphen))) {
            score += 2;
          }
          
          // Match in shop name (lower weight)
          const shopNameWithoutHyphen = shopName.replace(/-/g, '');
          if (shopName && (shopName.includes(tokenLower) || shopNameWithoutHyphen.includes(tokenWithoutHyphen))) {
            score += 2;
          }
          
          // Partial match in any field (fuzzy matching)
          const productTextWithoutHyphen = productText.replace(/-/g, '');
          if (productText.includes(tokenLower) || productTextWithoutHyphen.includes(tokenWithoutHyphen)) {
            score += 1;
          }
        }

        // Boost score for products that match the original query (not just synonyms)
        if (title && title.includes(lowerQuery)) {
          score += 5;
        }
        if (brand && brand.includes(lowerQuery)) {
          score += 3;
        }
        if (tags && tags.includes(lowerQuery)) {
          score += 4;
        }

        return { product, score };
      });

      // Filter out products with zero score (no matches)
      const matchedProducts = scoredProducts.filter(item => item.score > 0);

      // Sort by score (descending) and return products
      matchedProducts.sort((a, b) => b.score - a.score);

      const results = matchedProducts.map(item => item.product);
      
      // If no results found with intelligent search, try a simple fallback search
      // This ensures we don't miss products due to edge cases
      // IMPORTANT: Also check expanded tokens in fallback (e.g., "shoes" should match "sneakers")
      if (results.length === 0 && allVisibleProducts.length > 0) {
        const lowerQuery = sanitizedQuery.toLowerCase();
        const fallbackResults = allVisibleProducts.filter((product) => {
          const title = (product.title || '').toLowerCase();
          const description = (product.description || '').toLowerCase();
          const brand = (product.brand || '').toLowerCase();
          const shopName = (product.shopName || '').toLowerCase();
          const tags = (product.tags || []).map((t: any) => 
            typeof t === 'string' ? t.toLowerCase() : ''
          ).join(' ');
          const categories = (product.categories || []).map((c: any) => 
            typeof c === 'string' ? c.toLowerCase() : ''
          ).join(' ');
          
          // Remove hyphens for matching (e.g., "t-shirt" matches "tshirt")
          const titleNoHyphen = title.replace(/-/g, '');
          const brandNoHyphen = brand.replace(/-/g, '');
          const queryNoHyphen = lowerQuery.replace(/-/g, '');
          
          // Check against original query
          if (title.includes(lowerQuery) || titleNoHyphen.includes(queryNoHyphen) ||
              description.includes(lowerQuery) || 
              brand.includes(lowerQuery) || brandNoHyphen.includes(queryNoHyphen) ||
              shopName.includes(lowerQuery) ||
              tags.includes(lowerQuery) ||
              categories.includes(lowerQuery)) {
            return true;
          }
          
          // Also check against ALL expanded tokens (synonyms)
          // This ensures "shoes" matches "sneakers", "boots", etc.
          for (const token of finalTokens) {
            const tokenLower = token.toLowerCase();
            const tokenNoHyphen = tokenLower.replace(/-/g, '');
            
            if (title.includes(tokenLower) || titleNoHyphen.includes(tokenNoHyphen) ||
                description.includes(tokenLower) ||
                brand.includes(tokenLower) || brandNoHyphen.includes(tokenNoHyphen) ||
                shopName.includes(tokenLower) ||
                tags.includes(tokenLower) ||
                categories.includes(tokenLower)) {
              return true;
            }
          }
          
          return false;
        });
        
        // Return fallback results if found
        if (fallbackResults.length > 0) {
          // CRITICAL: Limit fallback results to prevent too many results
          return fallbackResults.slice(0, MAX_RESULTS_TO_RETURN);
        }
      }
      
      // CRITICAL: Limit final results to prevent API blocking and improve performance
      return results.slice(0, MAX_RESULTS_TO_RETURN);
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
        secureLog('warn', 'Permission denied for super search products');
        return [];
      }
      // Log errors without exposing sensitive details
      secureLog('error', 'Error in superSearchProducts', { 
        code: error?.code,
        errorType: error?.name
      });
      // Don't throw - return empty array instead to prevent breaking the UI
      return [];
    }
    })();

    // Timeout protection: If search takes too long, return empty results
    const timeoutPromise = new Promise<Product[]>((resolve) => {
      setTimeout(() => {
        secureLog('warn', 'Search timeout exceeded', { timeoutMs: SEARCH_TIMEOUT_MS });
        resolve([]);
      }, SEARCH_TIMEOUT_MS);
    });

    // Race between search and timeout
    try {
      return await Promise.race([searchPromise, timeoutPromise]);
    } catch (error: any) {
      // Handle any errors from the race
      secureLog('error', 'Search promise race error', { 
        code: error?.code,
        errorType: error?.name
      });
      return [];
    }
  },

  // Get products by category - updated to handle both categories array and categoryIds array
  // Only returns approved products or products without status
  async getProductsByCategory(category: string): Promise<Product[]> {
    try {
      // Try to find approved products by category name in the categories array
      const q1 = query(
        productsCollection,
        where("status", "==", "approved"),
        where("categories", "array-contains", category)
      );
      const querySnapshot1 = await getDocs(q1);
      const productsByCategory = querySnapshot1.docs.map(productFromSnap);

      // Also try to find approved products by category ID in the categoryIds array
      const q2 = query(
        productsCollection,
        where("status", "==", "approved"),
        where("categoryIds", "array-contains", category)
      );
      const querySnapshot2 = await getDocs(q2);
      const productsByCategoryId = querySnapshot2.docs.map(productFromSnap);

      // For backward compatibility: also get products without status that match category
      const q3 = query(
        productsCollection,
        where("categories", "array-contains", category)
      );
      const querySnapshot3 = await getDocs(q3);
      const allByCategory = querySnapshot3.docs.map(productFromSnap);
      
      const approvedIds = new Set([...productsByCategory, ...productsByCategoryId].map(p => p.id));
      const productsWithoutStatus = allByCategory.filter(
        (p) => !approvedIds.has(p.id) && (p.status === undefined || p.status === null)
      );

      // Combine and deduplicate results
      const allProducts = [...productsByCategory, ...productsByCategoryId, ...productsWithoutStatus];
      const uniqueProducts = allProducts.filter(
        (product, index, self) =>
          index === self.findIndex((p) => p.id === product.id)
      );

      // Final filter to ensure only visible products
      return uniqueProducts.filter(isProductVisible);
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
        secureLog('warn', 'Permission denied for products by category');
        return [];
      }
      throw error;
    }
  },

  // Get products by category name specifically - only returns approved products or products without status
  async getProductsByCategoryName(categoryName: string): Promise<Product[]> {
    try {
      // Query for approved products first
      const approvedQ = query(
        productsCollection,
        where("status", "==", "approved"),
        where("categories", "array-contains", categoryName)
      );
      const approvedSnapshot = await getDocs(approvedQ);
      const approvedProducts = approvedSnapshot.docs.map(productFromSnap);

      // Also get products without status (for backward compatibility)
      const allQ = query(
        productsCollection,
        where("categories", "array-contains", categoryName)
      );
      const allSnapshot = await getDocs(allQ);
      const allProducts = allSnapshot.docs.map(productFromSnap);
      
      const approvedIds = new Set(approvedProducts.map(p => p.id));
      const productsWithoutStatus = allProducts.filter(
        (p) => !approvedIds.has(p.id) && (p.status === undefined || p.status === null)
      );

      // Combine and filter by visibility
      const combined = [...approvedProducts, ...productsWithoutStatus];
      return combined.filter(isProductVisible);
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      if (error?.code === 'permission-denied' || error?.message?.includes('permissions')) {
        secureLog('warn', 'Permission denied for products by category name');
        return [];
      }
      throw error;
    }
  },

  // Update product stock quantity for a specific variant
  // SECURITY: IDOR Protection - Validates sellerId matches authenticated user
  async updateProductStock(
    productId: string,
    variantId: string,
    newQuantity: number
  ): Promise<void> {
    try {
      // SECURITY: Validate inputs
      if (!productId || !isValidDocumentId(productId)) {
        throw new Error('Invalid product ID format');
      }
      if (!variantId || typeof variantId !== 'string' || variantId.length < 1 || variantId.length > 150) {
        throw new Error('Invalid variant ID format');
      }
      if (!Number.isInteger(newQuantity) || newQuantity < 0 || newQuantity > 1000000) {
        throw new Error('Stock quantity must be a valid integer between 0 and 1,000,000');
      }

      // SECURITY: IDOR Protection - Verify user owns the product
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to update product stock');
      }

      const productRef = doc(db, "Products", productId);
      const productDoc = await getDoc(productRef);

      if (!productDoc.exists()) {
        throw new Error("Product not found");
      }

      const productData = productDoc.data();
      
      // SECURITY: Verify sellerId matches authenticated user
      if (productData.sellerId !== currentUser.uid) {
        secureLog('warn', 'IDOR attempt detected - unauthorized product stock update', {
          productId,
          variantId,
          requestedSellerId: productData.sellerId,
          currentUserId: currentUser.uid,
          action: 'updateProductStock'
        });
        throw new Error('You can only update stock for your own products');
      }

      // Validate variant exists
      const variantExists = productData.variants?.some((v: any) => v.id === variantId);
      if (!variantExists) {
        throw new Error('Variant not found');
      }

      const updatedVariants =
        productData.variants?.map((variant: any) => {
          if (variant.id === variantId) {
            return { ...variant, stockQty: newQuantity };
          }
          return variant;
        }) || [];

      const totalStock = updatedVariants.reduce(
        (sum: number, variant: any) => sum + (variant.stockQty ?? variant.stockQuantity ?? 0),
        0
      );

      await updateDoc(productRef, {
        variants: updatedVariants,
        totalStock,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      secureLog('error', 'Failed to update product stock', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },


  // Track product interaction for popularity algorithm
  async trackProductInteraction(params: {
    productId: string;
    userId: string;
    interactionType:
      | "view"
      | "click"
      | "purchase"
      | "add_to_cart"
      | "add_to_wishlist";
    metadata?: Record<string, any>;
  }): Promise<void> {
    try {
      const { productId, userId, interactionType, metadata } = params;

      // Create interaction record
      const interactionData = {
        productId,
        userId,
        interactionType,
        timestamp: serverTimestamp(),
        metadata: metadata || {},
      };

      // Add to interactions collection
      const interactionsCollection = collection(db, "productInteractions");
      await addDoc(interactionsCollection, interactionData);

      // Update product popularity score
      const productRef = doc(db, "Products", productId);
      const productDoc = await getDoc(productRef);

      if (productDoc.exists()) {
        const productData = productDoc.data();
        const currentScore = productData.popularityScore || 0;

        // Calculate new score based on interaction type
        let scoreIncrement = 0;
        switch (interactionType) {
          case "view":
            scoreIncrement = 1;
            break;
          case "click":
            scoreIncrement = 5;
            break;
          case "purchase":
            scoreIncrement = 20;
            break;
          case "add_to_cart":
            scoreIncrement = 10;
            break;
          case "add_to_wishlist":
            scoreIncrement = 8;
            break;
        }

        const newScore = currentScore + scoreIncrement;

        // Update product with new popularity score
        await updateDoc(productRef, {
          popularityScore: newScore,
          totalViews:
            (productData.totalViews || 0) +
            (interactionType === "view" ? 1 : 0),
          totalClicks:
            (productData.totalClicks || 0) +
            (interactionType === "click" ? 1 : 0),
          totalPurchases:
            (productData.totalPurchases || 0) +
            (interactionType === "purchase" ? 1 : 0),
          totalAddToCart:
            (productData.totalAddToCart || 0) +
            (interactionType === "add_to_cart" ? 1 : 0),
          totalAddToWishlist:
            (productData.totalAddToWishlist || 0) +
            (interactionType === "add_to_wishlist" ? 1 : 0),
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error: any) {
      // Suppress expected errors (permission-denied for unauthenticated users, network issues)
      const errorCode = error?.code || error?.errorCode || '';
      const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
      
      // These are expected and should be silent:
      // 1. Permission denied for unauthenticated users (expected behavior)
      // 2. Network errors from blocked requests
      if (
        errorCode === 'permission-denied' ||
        errorCode === 'PERMISSION_DENIED' ||
        errorMessage.includes('permission-denied') ||
        errorMessage.includes('permission denied') ||
        errorMessage.includes('err_blocked_by_client') ||
        errorMessage.includes('blocked by client') ||
        errorMessage.includes('network') ||
        errorMessage.includes('failed to fetch')
      ) {
        // These are expected and should be completely silent
        return;
      }
      
      // Only log unexpected errors in development
      if (process.env.NODE_ENV === 'development') {
        secureLog("error", "Error tracking product interaction:", error);
      }
      // Don't throw error to avoid breaking user experience
    }
  },

  // Get product variants with stock information
  async getProductVariants(productId: string): Promise<any[]> {
    try {
      // SECURITY: Validate productId format
      if (!productId || !isValidDocumentId(productId)) {
        throw new Error('Invalid product ID format');
      }

      const productRef = doc(db, "Products", productId);
      const productDoc = await getDoc(productRef);

      if (!productDoc.exists()) {
        throw new Error("Product not found");
      }

      const productData = productDoc.data();
      // Only return variants for approved products or products without status (public visibility)
      if (productData.status && productData.status !== 'approved') {
        throw new Error("Product not available");
      }

      return productData.variants || [];
    } catch (error) {
      secureLog('error', 'Failed to get product variants', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Seller-specific methods for managing products

  // Create simple product (no variants)
  // SECURITY: IDOR Protection - Validates sellerId matches authenticated user
  async createSimpleProduct(productData: {
    title: string;
    description?: string;
    price: number;
    discountPrice?: number;
    stockQty: number;
    categories?: string[];
    categoryIds?: string[];
    images?: string[];
    thumbnail?: string;
    sellerId: string;
    tags?: string[];
    weight?: number;
    weightUnit?: string;
    status?: string;
  }): Promise<string> {
    try {
      // SECURITY: IDOR Protection - Verify sellerId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to create products');
      }
      
      if (productData.sellerId !== currentUser.uid) {
        secureLog('warn', 'IDOR attempt detected - unauthorized product creation', {
          requestedSellerId: productData.sellerId,
          currentUserId: currentUser.uid,
          action: 'createSimpleProduct'
        });
        throw new Error('You can only create products for your own seller account');
      }

      // SECURITY: Input validation
      const titleValidation = validateString(productData.title, 'Product title', 1, 200);
      if (!titleValidation.valid) {
        throw new Error(titleValidation.message || 'Invalid product title');
      }

      if (!Number.isFinite(productData.price) || productData.price < 0 || productData.price > 10000000) {
        throw new Error('Price must be a valid number between 0 and 10,000,000');
      }

      if (productData.discountPrice !== undefined) {
        if (!Number.isFinite(productData.discountPrice) || productData.discountPrice < 0 || productData.discountPrice > productData.price) {
          throw new Error('Discount price must be a valid number between 0 and the regular price');
        }
      }

      if (!Number.isInteger(productData.stockQty) || productData.stockQty < 0 || productData.stockQty > 1000000) {
        throw new Error('Stock quantity must be a valid integer between 0 and 1,000,000');
      }

      // SECURITY: Rate limiting
      const rateLimitKey = `create_product_${currentUser.uid}`;
      const rateLimitResult = firebaseRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many product creation attempts. Please try again later.');
      }

      const docRef = await addDoc(productsCollection, {
        ...productData,
        productType: "simple",
        totalStock: productData.stockQty,
        variants: [],
        soldQuantity: 0,
        isFeatured: false,
        isPopular: false,
        popularityScore: 0,
        totalViews: 0,
        totalClicks: 0,
        totalPurchases: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      secureLog('error', 'Failed to create simple product', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Create variable product (with variants)
  // SECURITY: IDOR Protection - Validates sellerId matches authenticated user
  async createVariableProduct(productData: {
    title: string;
    description?: string;
    basePrice: number;
    categories?: string[];
    categoryIds?: string[];
    images?: string[];
    thumbnail?: string;
    sellerId: string;
    tags?: string[];
    weight?: number;
    weightUnit?: string;
    status?: string;
    productAttributes?: Array<{
      Name: string;
      Values: string[];
    }>;
    variants: Array<{
      size?: string;
      color?: string;
      stockQuantity: number;
      price?: number;
      sku?: string;
      image?: string;
    }>;
  }): Promise<string> {
    try {
      // SECURITY: IDOR Protection - Verify sellerId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to create products');
      }
      
      if (productData.sellerId !== currentUser.uid) {
        secureLog('warn', 'IDOR attempt detected - unauthorized product creation', {
          requestedSellerId: productData.sellerId,
          currentUserId: currentUser.uid,
          action: 'createVariableProduct'
        });
        throw new Error('You can only create products for your own seller account');
      }

      // SECURITY: Input validation
      const titleValidation = validateString(productData.title, 'Product title', 1, 200);
      if (!titleValidation.valid) {
        throw new Error(titleValidation.message || 'Invalid product title');
      }

      if (!Number.isFinite(productData.basePrice) || productData.basePrice < 0 || productData.basePrice > 10000000) {
        throw new Error('Base price must be a valid number between 0 and 10,000,000');
      }

      if (!Array.isArray(productData.variants) || productData.variants.length === 0) {
        throw new Error('Variable products must have at least one variant');
      }

      if (productData.variants.length > 100) {
        throw new Error('Variable products cannot have more than 100 variants');
      }

      // Validate each variant
      for (const variant of productData.variants) {
        if (!Number.isInteger(variant.stockQuantity) || variant.stockQuantity < 0 || variant.stockQuantity > 1000000) {
          throw new Error('Variant stock quantity must be a valid integer between 0 and 1,000,000');
        }
        if (variant.price !== undefined) {
          if (!Number.isFinite(variant.price) || variant.price < 0 || variant.price > 10000000) {
            throw new Error('Variant price must be a valid number between 0 and 10,000,000');
          }
        }
      }

      // SECURITY: Rate limiting
      const rateLimitKey = `create_product_${currentUser.uid}`;
      const rateLimitResult = firebaseRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many product creation attempts. Please try again later.');
      }

      // Transform variants to database structure
      const variants = Array.isArray(productData.variants)
        ? productData.variants
        : [];
      const transformedVariants = variants.map((variant) => ({
        attributeValues: {
          Size: variant.size || null,
          Color: variant.color || null,
        },
        stockQty: variant.stockQuantity,
        priceOverride: variant.price || null,
        sku: variant.sku || "",
        image: variant.image || "",
        soldQuantity: 0,
        barcode: null,
      }));

      const totalStock = productData.variants.reduce(
        (sum, variant) => sum + variant.stockQuantity,
        0
      );

      const docRef = await addDoc(productsCollection, {
        ...productData,
        price: productData.basePrice,
        productType: "variable",
        totalStock,
        variants: transformedVariants,
        soldQuantity: 0,
        isFeatured: false,
        isPopular: false,
        popularityScore: 0,
        totalViews: 0,
        totalClicks: 0,
        totalPurchases: 0,
        isFlashSale: false,
        rating: 0,
        totalReviews: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      secureLog('error', 'Failed to create variable product', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Update simple product stock
  // SECURITY: IDOR Protection - Validates sellerId matches authenticated user
  async updateSimpleProductStock(
    productId: string,
    newStockQty: number
  ): Promise<void> {
    try {
      // SECURITY: Validate inputs
      if (!productId || !isValidDocumentId(productId)) {
        throw new Error('Invalid product ID format');
      }

      if (!Number.isInteger(newStockQty) || newStockQty < 0 || newStockQty > 1000000) {
        throw new Error('Stock quantity must be a valid integer between 0 and 1,000,000');
      }

      // SECURITY: IDOR Protection - Verify user owns the product
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to update product stock');
      }

      const productRef = doc(productsCollection, productId);
      const productDoc = await getDoc(productRef);

      if (!productDoc.exists()) {
        throw new Error("Product not found");
      }

      const productData = productDoc.data();
      if (productData.productType !== "simple") {
        throw new Error("Product is not a simple product");
      }

      // SECURITY: Verify sellerId matches authenticated user
      if (productData.sellerId !== currentUser.uid) {
        secureLog('warn', 'IDOR attempt detected - unauthorized product stock update', {
          productId,
          requestedSellerId: productData.sellerId,
          currentUserId: currentUser.uid,
          action: 'updateSimpleProductStock'
        });
        throw new Error('You can only update stock for your own products');
      }

      await updateDoc(productRef, {
        stockQty: newStockQty,
        totalStock: newStockQty,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      secureLog('error', 'Failed to update simple product stock', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Update variable product variant stock
  // SECURITY: IDOR Protection - Validates sellerId matches authenticated user
  async updateVariableProductVariantStock(
    productId: string,
    variantIndex: number,
    newStockQuantity: number
  ): Promise<void> {
    try {
      // SECURITY: Validate inputs
      if (!productId || !isValidDocumentId(productId)) {
        throw new Error('Invalid product ID format');
      }

      if (!Number.isInteger(variantIndex) || variantIndex < 0) {
        throw new Error('Invalid variant index');
      }

      if (!Number.isInteger(newStockQuantity) || newStockQuantity < 0 || newStockQuantity > 1000000) {
        throw new Error('Stock quantity must be a valid integer between 0 and 1,000,000');
      }

      // SECURITY: IDOR Protection - Verify user owns the product
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to update product stock');
      }

      const productRef = doc(productsCollection, productId);
      const productDoc = await getDoc(productRef);

      if (!productDoc.exists()) {
        throw new Error("Product not found");
      }

      const productData = productDoc.data();
      if (productData.productType !== "variable") {
        throw new Error("Product is not a variable product");
      }

      // SECURITY: Verify sellerId matches authenticated user
      if (productData.sellerId !== currentUser.uid) {
        secureLog('warn', 'IDOR attempt detected - unauthorized variant stock update', {
          productId,
          requestedSellerId: productData.sellerId,
          currentUserId: currentUser.uid,
          action: 'updateVariableProductVariantStock'
        });
        throw new Error('You can only update stock for your own products');
      }

      // Validate variant index exists
      if (!productData.variants || variantIndex >= productData.variants.length) {
        throw new Error('Variant index out of range');
      }

      const updatedVariants =
        productData.variants?.map((variant: any, index: number) => {
          if (index === variantIndex) {
            return { ...variant, stockQty: newStockQuantity };
          }
          return variant;
        }) || [];

      const totalStock = updatedVariants.reduce(
        (sum: number, variant: any) =>
          sum + (variant.stockQty ?? variant.stockQuantity ?? 0), // Default to 0 if not set
        0
      );

      await updateDoc(productRef, {
        variants: updatedVariants,
        totalStock,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      secureLog('error', 'Failed to update variable product variant stock', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Add variant to variable product
  // SECURITY: IDOR Protection - Validates sellerId matches authenticated user
  async addVariantToProduct(
    productId: string,
    variantData: {
      size?: string;
      color?: string;
      stockQuantity: number;
      price?: number;
      sku?: string;
      image?: string;
    }
  ): Promise<void> {
    try {
      // SECURITY: Validate inputs
      if (!productId || !isValidDocumentId(productId)) {
        throw new Error('Invalid product ID format');
      }

      if (!Number.isInteger(variantData.stockQuantity) || variantData.stockQuantity < 0 || variantData.stockQuantity > 1000000) {
        throw new Error('Stock quantity must be a valid integer between 0 and 1,000,000');
      }

      if (variantData.price !== undefined) {
        if (!Number.isFinite(variantData.price) || variantData.price < 0 || variantData.price > 10000000) {
          throw new Error('Price must be a valid number between 0 and 10,000,000');
        }
      }

      // SECURITY: IDOR Protection - Verify user owns the product
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to add variants');
      }

      const productRef = doc(productsCollection, productId);
      const productDoc = await getDoc(productRef);

      if (!productDoc.exists()) {
        throw new Error("Product not found");
      }

      const productData = productDoc.data();
      if (productData.productType !== "variable") {
        throw new Error("Product is not a variable product");
      }

      // SECURITY: Verify sellerId matches authenticated user
      if (productData.sellerId !== currentUser.uid) {
        secureLog('warn', 'IDOR attempt detected - unauthorized variant addition', {
          productId,
          requestedSellerId: productData.sellerId,
          currentUserId: currentUser.uid,
          action: 'addVariantToProduct'
        });
        throw new Error('You can only add variants to your own products');
      }

      // Limit total variants
      const currentVariants = productData.variants || [];
      if (currentVariants.length >= 100) {
        throw new Error('Maximum variant limit (100) reached');
      }

      // Transform to database structure
      const newVariant = {
        attributeValues: {
          Size: variantData.size || null,
          Color: variantData.color || null,
        },
        stockQty: variantData.stockQuantity,
        priceOverride: variantData.price || null,
        sku: variantData.sku || "",
        image: variantData.image || "",
        soldQuantity: 0,
        barcode: null,
      };

      const updatedVariants = [...currentVariants, newVariant];
      const totalStock = updatedVariants.reduce(
        (sum: number, variant: any) =>
          sum + (variant.stockQty ?? variant.stockQuantity ?? 0), // Default to 0 if not set
        0
      );

      await updateDoc(productRef, {
        variants: updatedVariants,
        totalStock,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      secureLog('error', 'Failed to add variant to product', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Remove variant from variable product
  // SECURITY: IDOR Protection - Validates sellerId matches authenticated user
  async removeVariantFromProduct(
    productId: string,
    variantIndex: number
  ): Promise<void> {
    try {
      // SECURITY: Validate inputs
      if (!productId || !isValidDocumentId(productId)) {
        throw new Error('Invalid product ID format');
      }

      if (!Number.isInteger(variantIndex) || variantIndex < 0) {
        throw new Error('Invalid variant index');
      }

      // SECURITY: IDOR Protection - Verify user owns the product
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to remove variants');
      }

      const productRef = doc(productsCollection, productId);
      const productDoc = await getDoc(productRef);

      if (!productDoc.exists()) {
        throw new Error("Product not found");
      }

      const productData = productDoc.data();
      if (productData.productType !== "variable") {
        throw new Error("Product is not a variable product");
      }

      // SECURITY: Verify sellerId matches authenticated user
      if (productData.sellerId !== currentUser.uid) {
        secureLog('warn', 'IDOR attempt detected - unauthorized variant removal', {
          productId,
          requestedSellerId: productData.sellerId,
          currentUserId: currentUser.uid,
          action: 'removeVariantFromProduct'
        });
        throw new Error('You can only remove variants from your own products');
      }

      // Validate variant index exists
      if (!productData.variants || variantIndex >= productData.variants.length) {
        throw new Error('Variant index out of range');
      }

      // Prevent removing last variant
      if (productData.variants.length <= 1) {
        throw new Error('Cannot remove the last variant. Delete the product instead.');
      }

      const updatedVariants =
        productData.variants?.filter(
          (variant: any, index: number) => index !== variantIndex
        ) || [];
      const totalStock = updatedVariants.reduce(
        (sum: number, variant: any) =>
          sum + (variant.stockQty ?? variant.stockQuantity ?? 0), // Default to 0 if not set
        0
      );

      await updateDoc(productRef, {
        variants: updatedVariants,
        totalStock,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      secureLog('error', 'Failed to remove variant from product', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Get products by seller - only returns approved products or products without status
  async getProductsBySeller(
    sellerId: string,
    productType?: "simple" | "variable"
  ): Promise<Product[]> {
    try {
      // Query for approved products first
      let approvedQ = query(
        productsCollection,
        where("status", "==", "approved"),
        where("sellerId", "==", sellerId)
      );

      if (productType) {
        approvedQ = query(approvedQ, where("productType", "==", productType));
      }

      const approvedSnapshot = await getDocs(approvedQ);
      const approvedProducts = approvedSnapshot.docs.map(productFromSnap);

      // Also get products without status (for backward compatibility)
      let allQ = query(productsCollection, where("sellerId", "==", sellerId));

      if (productType) {
        allQ = query(allQ, where("productType", "==", productType));
      }

      const allSnapshot = await getDocs(allQ);
      const allProducts = allSnapshot.docs.map(productFromSnap);
      
      const approvedIds = new Set(approvedProducts.map(p => p.id));
      const productsWithoutStatus = allProducts.filter(
        (p) => !approvedIds.has(p.id) && (p.status === undefined || p.status === null)
      );

      // Combine and filter by visibility
      const combined = [...approvedProducts, ...productsWithoutStatus];
      return combined.filter(isProductVisible);
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      if (
        error?.code === "permission-denied" ||
        error?.message?.includes("permissions")
      ) {
        secureLog('warn', "Permission denied for products by seller - user not authenticated, returning empty array");
        return [];
      }
      throw error;
    }
  },

  // Get products by category ID with pagination
  async getProductsByCategoryId({
    categoryId,
    pageSize = 20,
    cursor,
  }: {
    categoryId: string;
    pageSize?: number;
    cursor?: any;
  }): Promise<{ items: Product[]; nextCursor: any }> {
    try {
      let q = query(
        productsCollection,
        where("status", "==", "approved"),
        where("categoryIds", "array-contains", categoryId),
        orderBy("updatedAt", "desc"),
        limit(pageSize)
      );

      if (cursor) {
        q = query(q, startAfter(cursor));
      }

      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(productFromSnap);
      const nextCursor =
        querySnapshot.docs.length === pageSize
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      return { items, nextCursor };
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      if (
        error?.code === "permission-denied" ||
        error?.message?.includes("permissions")
      ) {
        secureLog('warn', "Permission denied for products by category ID - user not authenticated, returning empty array");
        return { items: [], nextCursor: null };
      }
      throw error;
    }
  },

  // Get featured products with limit (matches Flutter: isFeatured == true, status == 'approved', orderBy createdAt desc, limit ~20)
  // PERFORMANCE OPTIMIZATION: Reduced default limit and optimized query
  async getFeaturedProducts(limitCount: number = 12): Promise<Product[]> {
    try {
      // PERFORMANCE: Use smaller default limit for faster initial load
      // Most homepages only show 6-12 featured products anyway
      const effectiveLimit = Math.min(limitCount, 20); // Cap at 20 for performance
      
      const q = query(
        productsCollection,
        where("status", "==", "approved"),
        where("isFeatured", "==", true),
        orderBy("createdAt", "desc"),
        limit(effectiveLimit)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(productFromSnap);
    } catch (error: any) {
      // Handle permission errors gracefully for non-authenticated users
      if (
        error?.code === "permission-denied" ||
        error?.message?.includes("permissions")
      ) {
        secureLog('warn', "Permission denied for featured products - user not authenticated, returning empty array");
        return [];
      }
      // Handle missing index errors - fallback to featured without orderBy (faster)
      if (error?.code === "failed-precondition") {
        secureLog('warn', "Index missing for featured products query, using fallback");
        try {
          // PERFORMANCE: Fallback query without orderBy is faster (no index needed)
          const fallbackQ = query(
            productsCollection,
            where("status", "==", "approved"),
            where("isFeatured", "==", true),
            limit(Math.min(limitCount, 20))
          );
          const fallbackSnapshot = await getDocs(fallbackQ);
          const products = fallbackSnapshot.docs.map(productFromSnap);
          // Client-side sort by createdAt desc (only if we have the data)
          return products.sort((a, b) => {
            const aDate = a.createdAt ? new Date(a.createdAt as any).getTime() : 0;
            const bDate = b.createdAt ? new Date(b.createdAt as any).getTime() : 0;
            return bDate - aDate;
          }).slice(0, limitCount); // Ensure we don't return more than requested
        } catch (fallbackError) {
          secureLog('error', "Fallback query also failed", { error: fallbackError });
          return [];
        }
      }
      throw error;
    }
  },

  // Fetch popular products - tries multiple fields to match Flutter app behavior
  // Priority: purchaseCount > ordersCount > soldQuantity > totalPurchases
  async fetchPopularProducts(limitN: number = 40): Promise<Product[]> {
    // Try different fields in order of preference to match Flutter app
    const fieldOrder = ['purchaseCount', 'ordersCount', 'soldQuantity', 'totalPurchases'];
    
    for (const field of fieldOrder) {
      try {
        const q = query(
          productsCollection,
          where("status", "==", "approved"),
          orderBy(field, "desc"),
          limit(limitN)
        );

        const querySnapshot = await getDocs(q);
        const products = querySnapshot.docs.map(productFromSnap);
        
        // If we got products, return them
        if (products.length > 0) {
          return products;
        }
      } catch (error: any) {
        // Handle permission errors gracefully - return empty array for unauthenticated users
        if (
          error?.code === "permission-denied" ||
          error?.message?.includes("permissions")
        ) {
          // Silently handle - this is expected for unauthenticated users
          // Don't log warnings for expected permission-denied errors
          return [];
        }
        // If it's not an index error, try next field
        if (error?.code !== "failed-precondition") {
          continue;
        }
        // If it's the last field and it's an index error, fall back to featured
        if (field === fieldOrder[fieldOrder.length - 1]) {
          secureLog('warn', `All popular product queries failed. Last attempt (${field}) missing index, falling back to featured`);
          try {
            return await this.getFeaturedProducts(limitN);
          } catch (fallbackError) {
            secureLog('error', "Fallback to featured also failed", { error: fallbackError });
            return [];
          }
        }
        // Otherwise, try next field
        continue;
      }
    }
    
    // If all fields failed (no index errors but no results), try client-side sorting
    try {
      secureLog('warn', "All popular product queries returned empty. Trying client-side sort fallback");
      const q = query(
        productsCollection,
        where("status", "==", "approved"),
        limit(limitN * 2) // Get more to sort client-side
      );
      
      const querySnapshot = await getDocs(q);
      const products = querySnapshot.docs.map(productFromSnap);
      
      // Sort by purchaseCount, then ordersCount, then soldQuantity
      products.sort((a, b) => {
        const aScore = (a.purchaseCount || 0) * 1000 + (a.ordersCount || 0) * 100 + (a.soldQuantity || 0);
        const bScore = (b.purchaseCount || 0) * 1000 + (b.ordersCount || 0) * 100 + (b.soldQuantity || 0);
        return bScore - aScore;
      });
      
      return products.slice(0, limitN);
    } catch (error: any) {
      // Handle permission errors gracefully
      if (
        error?.code === "permission-denied" ||
        error?.message?.includes("permissions")
      ) {
        // Silently handle - this is expected for unauthenticated users
        // Don't log warnings for expected permission-denied errors
        return [];
      }
      // Final fallback to featured products
      secureLog('warn', "Client-side sort also failed, falling back to featured products");
      try {
        return await this.getFeaturedProducts(limitN);
      } catch (fallbackError) {
        secureLog('error', "Fallback to featured also failed", { error: fallbackError });
        return [];
      }
    }
  },

  // Fetch recent arrivals (matches Flutter: status == 'approved', orderBy createdAt desc)
  async fetchRecentArrivals(limitN: number = 20): Promise<Product[]> {
    try {
      const q = query(
        productsCollection,
        where("status", "==", "approved"),
        orderBy("createdAt", "desc"),
        limit(limitN)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(productFromSnap);
    } catch (error: any) {
      // Handle permission errors gracefully
      if (
        error?.code === "permission-denied" ||
        error?.message?.includes("permissions")
      ) {
        secureLog('warn', "Permission denied for recent arrivals");
        return [];
      }
      // Handle missing index errors - fallback to featured products
      if (error?.code === "failed-precondition") {
        secureLog('warn', "Index missing for recent arrivals query, falling back to featured");
        try {
          return await this.getFeaturedProducts(limitN);
        } catch (fallbackError) {
          secureLog('error', "Fallback to featured also failed", { error: fallbackError });
          return [];
        }
      }
      throw error;
    }
  },

  // Fetch recent purchases (matches Flutter: status == 'approved', orderBy lastPurchasedAt desc)
  async fetchRecentPurchases(limitN: number = 20): Promise<Product[]> {
    try {
      const q = query(
        productsCollection,
        where("status", "==", "approved"),
        orderBy("lastPurchasedAt", "desc"),
        limit(limitN)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(productFromSnap);
    } catch (error: any) {
      // Handle permission errors gracefully
      if (
        error?.code === "permission-denied" ||
        error?.message?.includes("permissions")
      ) {
        secureLog('warn', "Permission denied for recent purchases");
        return [];
      }
      // Handle missing index or null lastPurchasedAt - fallback to recent arrivals
      if (error?.code === "failed-precondition") {
        secureLog('warn', "Index missing for recent purchases query, falling back to recent arrivals");
        try {
          return await this.fetchRecentArrivals(limitN);
        } catch (fallbackError) {
          secureLog('error', "Fallback to recent arrivals also failed", { error: fallbackError });
          return [];
        }
      }
      throw error;
    }
  },

  // Get low stock products for seller (stock < threshold)
  // SECURITY: IDOR Protection - Validates sellerId matches authenticated user
  async getLowStockProducts(
    sellerId: string,
    threshold: number = 10
  ): Promise<Product[]> {
    try {
      // SECURITY: Validate inputs
      if (!sellerId || !isValidDocumentId(sellerId)) {
        throw new Error('Invalid seller ID format');
      }

      if (!Number.isFinite(threshold) || threshold < 0 || threshold > 1000000) {
        throw new Error('Threshold must be a valid number between 0 and 1,000,000');
      }

      // SECURITY: IDOR Protection - Verify sellerId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to view stock information');
      }
      
      if (sellerId !== currentUser.uid) {
        secureLog('warn', 'IDOR attempt detected - unauthorized stock access', {
          requestedSellerId: sellerId,
          currentUserId: currentUser.uid,
          action: 'getLowStockProducts'
        });
        throw new Error('You can only view stock information for your own seller account');
      }

      const products = await this.getProductsBySeller(sellerId);

      return products.filter((product: Product) => {
        if (product.productType === "simple") {
          return (product.stockQty ?? 0) < threshold;
        } else {
          return (product.totalStock ?? 0) < threshold;
        }
      });
    } catch (error) {
      secureLog('error', 'Failed to get low stock products', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Get out of stock products for seller
  // SECURITY: IDOR Protection - Validates sellerId matches authenticated user
  async getOutOfStockProducts(sellerId: string): Promise<Product[]> {
    try {
      // SECURITY: Validate inputs
      if (!sellerId || !isValidDocumentId(sellerId)) {
        throw new Error('Invalid seller ID format');
      }

      // SECURITY: IDOR Protection - Verify sellerId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to view stock information');
      }
      
      if (sellerId !== currentUser.uid) {
        secureLog('warn', 'IDOR attempt detected - unauthorized stock access', {
          requestedSellerId: sellerId,
          currentUserId: currentUser.uid,
          action: 'getOutOfStockProducts'
        });
        throw new Error('You can only view stock information for your own seller account');
      }

      const products = await this.getProductsBySeller(sellerId);

      return products.filter((product: Product) => {
        if (product.productType === "simple") {
          return (product.stockQty ?? 0) <= 0;
        } else {
          return (product.totalStock ?? 0) <= 0;
        }
      });
    } catch (error) {
      secureLog('error', 'Failed to get out of stock products', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Get filtered products with server-side filtering (optimized for categories page)
  async getFilteredProducts({
    categories = [],
    priceRange = [0, 50000],
    rating,
    availability = 'all',
    brands = [],
    sizes = [],
    colors = [],
    sortBy = 'default',
    pageSize = 20,
    cursor,
  }: {
    categories?: string[];
    priceRange?: [number, number];
    rating?: number;
    availability?: 'inStock' | 'outOfStock' | 'all';
    brands?: string[];
    sizes?: string[];
    colors?: string[];
    sortBy?: 'default' | 'priceLow' | 'priceHigh' | 'newest' | 'rating';
    pageSize?: number;
    cursor?: any;
  }): Promise<{ items: Product[]; nextCursor: any; hasMore: boolean }> {
    try {
      // Early exit for invalid page size
      if (pageSize <= 0 || pageSize > 100) {
        return { items: [], nextCursor: null, hasMore: false };
      }

      // Start with base query - only approved products
      let q = query(productsCollection, where("status", "==", "approved"));

      // Add category filter (array-contains-any for multiple categories)
      if (categories.length > 0) {
        // Use array-contains-any for multiple categories (more efficient than multiple queries)
        q = query(q, where("categoryIds", "array-contains-any", categories.slice(0, 10))); // Limit to 10 for Firestore constraints
      }

      // Add price range filters
      if (priceRange[0] > 0) {
        q = query(q, where("price", ">=", priceRange[0]));
      }
      if (priceRange[1] < 50000) {
        q = query(q, where("price", "<=", priceRange[1]));
      }

      // Add brand filter (if supported in schema)
      if (brands.length > 0) {
        q = query(q, where("brand", "in", brands.slice(0, 10))); // Limit for Firestore constraints
      }

      // Determine sort order
      let sortField = "updatedAt";
      let sortOrder: "asc" | "desc" = "desc";

      switch (sortBy) {
        case 'priceLow':
          sortField = "price";
          sortOrder = "asc";
          break;
        case 'priceHigh':
          sortField = "price";
          sortOrder = "desc";
          break;
        case 'newest':
          sortField = "createdAt";
          sortOrder = "desc";
          break;
        case 'rating':
          sortField = "averageRating";
          sortOrder = "desc";
          break;
        default:
          sortField = "updatedAt";
          sortOrder = "desc";
      }

      // Add sorting
      q = query(q, orderBy(sortField, sortOrder));

      // Add pagination
      q = query(q, limit(pageSize + 1));
      if (cursor) {
        q = query(q, startAfter(cursor));
      }

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;

      if (docs.length === 0) {
        return { items: [], nextCursor: null, hasMore: false };
      }

      // Get products and apply client-side filters that can't be done server-side
      let products = docs.slice(0, pageSize).map(productFromSnap);

      // Apply rating filter (client-side since Firestore doesn't support float comparisons well)
      if (rating) {
        products = products.filter(product => (product.rating ?? 0) >= rating!);
      }

      // Apply availability filter
      if (availability !== 'all') {
        products = products.filter(product => {
          const isInStock = product.productType === "simple"
            ? (product.stockQty ?? 0) > 0
            : (product.totalStock ?? 0) > 0;
          return availability === 'inStock' ? isInStock : !isInStock;
        });
      }

      // Apply size and color filters (client-side array matching)
      if (sizes.length > 0) {
        products = products.filter(product =>
          product.sizes?.some(size => sizes.includes(size)) ?? false
        );
      }

      if (colors.length > 0) {
        products = products.filter(product =>
          product.colors?.some(color => colors.includes(color)) ?? false
        );
      }

      // Check if there are more items
      const hasMore = docs.length > pageSize;
      const nextCursor = hasMore ? docs[pageSize - 1] : null;

      return {
        items: products,
        nextCursor,
        hasMore
      };
    } catch (error: any) {
      secureLog('error', 'Failed to get filtered products', { error: sanitizeError(error) });
      // Return empty result instead of throwing to prevent UI crashes
      return { items: [], nextCursor: null, hasMore: false };
    }
  },
};

// User services - corrected according to FIRESTORE_CONNECTIONS.md
export const userService = {
  // Security: Enhanced create or update user profile
  async createUserProfile(userData: UserProfile): Promise<string> {
    try {
      // Security: Enhanced validate user data
      const uidValidation = validateString(userData.uid, 'User ID', 1, 50);
      if (!uidValidation.valid) {
        throw new Error(uidValidation.message || 'Invalid user ID');
      }

      const emailValidation = validateString(userData.email, 'Email', 1, 254);
      if (!emailValidation.valid) {
        throw new Error(emailValidation.message || 'Invalid email');
      }

      // Security: Rate limiting
      const rateLimitKey = `create_profile_${userData.uid}`;
      const rateLimitResult = firebaseRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many profile creation attempts. Please try again later.');
      }

      // Removed debug log to prevent sensitive data exposure

      const docRef = await addDoc(usersCollection, {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Removed debug log to prevent sensitive data exposure

      return docRef.id;
    } catch (error) {
      secureLog('error', 'Failed to create user profile', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Security: Enhanced get user profile
  // SECURITY: IDOR Protection - This function should only be called with currentUser.uid
  // Firestore rules provide additional protection, but this function validates ownership when currentUserUid is provided
  async getUserProfile(uid: string, currentUserUid?: string): Promise<UserProfile | null> {
    try {
      // Security: Enhanced validate user ID
      const uidValidation = validateString(uid, 'User ID', 1, 50);
      if (!uidValidation.valid) {
        throw new Error(uidValidation.message || 'Invalid user ID');
      }

      if (!isValidDocumentId(uid)) {
        throw new Error('Invalid user ID format');
      }

      // SECURITY: IDOR Protection - Verify ownership if currentUserUid is provided
      // Note: Firestore rules also protect this, but adding explicit check for defense-in-depth
      if (currentUserUid && currentUserUid !== uid) {
        // Allow access to seller profiles (public for marketplace)
        // For other profiles, require ownership
        // This check will be enforced by Firestore rules, but we log it here
        secureLog('warn', 'getUserProfile: Attempted to access different user profile', {
          requestedUid: sanitizeString(uid),
          currentUserUid: sanitizeString(currentUserUid)
        });
        // Still allow the request - Firestore rules will enforce authorization
        // This is a frontend function, so Firestore rules are the primary protection
      }

      // Security: Rate limiting
      const rateLimitKey = `get_profile_${uid}`;
      const rateLimitResult = firebaseRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many profile fetch attempts. Please try again later.');
      }

      // Removed debug log to prevent sensitive data exposure (userId)

      // Prefer direct doc lookup (Users/{uid}); older docs may miss 'uid' field
      const byIdRef = doc(db, "Users", uid);
      const byIdSnap = await getDoc(byIdRef);
      if (byIdSnap.exists()) {
        const profileData = byIdSnap.data() as UserProfile;
        // Removed debug log to prevent excessive console output
        return profileData;
      }
      // Fallback: query by 'uid' field if document id isn't uid
      const q = query(usersCollection, where("uid", "==", uid));
      const qs = await getDocs(q);
      if (!qs.empty) {
        const profileData = qs.docs[0].data() as unknown as UserProfile;
        // Removed debug log to prevent excessive console output
        return profileData;
      }
      return null;
    } catch (error) {
      secureLog('error', 'Failed to get user profile', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Security: Enhanced update user profile
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async updateUserProfile(userId: string, userData: Partial<UserProfile>): Promise<void> {
    try {
      // Security: Enhanced validate user ID
      const userIdValidation = validateString(userId, 'User ID', 1, 50);
      if (!userIdValidation.valid) {
        throw new Error(userIdValidation.message || 'Invalid user ID');
      }

      if (!isValidDocumentId(userId)) {
        throw new Error('Invalid user ID format');
      }
      
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      try {
        verifyUserIdOwnership(userId, {
          action: 'update profile',
          resourceType: 'profile'
        });
      } catch (error: any) {
        throw new Error(error.message || 'Unauthorized access to profile');
      }

      // Security: Rate limiting
      const rateLimitKey = `update_profile_${userId}`;
      const rateLimitResult = firebaseRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many profile update attempts. Please try again later.');
      }

      // Removed debug log to prevent sensitive data exposure

      const docRef = doc(db, 'Users', userId);
      await updateDoc(docRef, {
        ...userData,
        updatedAt: serverTimestamp()
      });

      // Removed debug log to prevent sensitive data exposure
    } catch (error) {
      secureLog('error', 'Failed to update user profile', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Security: Enhanced upload profile photo to Firebase Storage
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async uploadProfilePhoto(userId: string, file: File): Promise<string> {
    try {
      // Security: Enhanced validate user ID
      const userIdValidation = validateString(userId, 'User ID', 1, 50);
      if (!userIdValidation.valid) {
        throw new Error(userIdValidation.message || 'Invalid user ID');
      }

      if (!isValidDocumentId(userId)) {
        throw new Error('Invalid user ID format');
      }
      
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      try {
        verifyUserIdOwnership(userId, {
          action: 'upload profile photo',
          resourceType: 'profile'
        });
      } catch (error: any) {
        throw new Error(error.message || 'Unauthorized access to profile');
      }

      // Security: Enhanced validate file
      const fileValidation = validateFile(file);
      if (!fileValidation.valid) {
        throw new Error(fileValidation.error || 'Invalid file');
      }

      // Security: Rate limiting
      const rateLimitKey = `upload_photo_${userId}`;
      const rateLimitResult = firebaseRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many photo upload attempts. Please try again later.');
      }

      // Removed debug log to prevent sensitive data exposure

      // Create a unique filename
      const timestamp = Date.now();
      // SECURITY: Validate and sanitize file extension to prevent path traversal
      const fileExtension = file.name.split(".").pop() || "jpg";
      // SECURITY: Validate file extension (only allow safe image extensions)
      const allowedExtensions = ["jpg", "jpeg", "png", "gif", "webp"];
      const sanitizedExtension = allowedExtensions.includes(fileExtension.toLowerCase()) 
        ? fileExtension.toLowerCase() 
        : "jpg";
      
      // SECURITY: Validate userId format (already validated above, but double-check)
      if (!isValidDocumentId(userId)) {
        throw new Error('Invalid user ID format');
      }
      
      // SECURITY: Construct safe storage path
      const fileName = `profile-photos/${userId}/${timestamp}.${sanitizedExtension}`;
      
      // SECURITY: Final validation of constructed path
      if (fileName.includes("..") || fileName.includes("//") || fileName.length > 500) {
        throw new Error('Invalid file path');
      }

      // Create storage reference
      const storageRef = ref(storage, fileName);

      // Upload file
      const snapshot = await uploadBytes(storageRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Removed debug log to prevent sensitive data exposure

      return downloadURL;
    } catch (error) {
      secureLog('error', 'Failed to upload profile photo', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Security: Enhanced delete profile photo from Firebase Storage
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async deleteProfilePhoto(userId: string): Promise<void> {
    try {
      // Security: Enhanced validate user ID
      const userIdValidation = validateString(userId, 'User ID', 1, 50);
      if (!userIdValidation.valid) {
        throw new Error(userIdValidation.message || 'Invalid user ID');
      }

      if (!isValidDocumentId(userId)) {
        throw new Error('Invalid user ID format');
      }
      
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      try {
        verifyUserIdOwnership(userId, {
          action: 'delete profile photo',
          resourceType: 'profile'
        });
      } catch (error: any) {
        throw new Error(error.message || 'Unauthorized access to profile');
      }

      // Security: Rate limiting
      const rateLimitKey = `delete_photo_${userId}`;
      const rateLimitResult = firebaseRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many photo deletion attempts. Please try again later.');
      }

      // Removed debug log to prevent sensitive data exposure

      // Get current user profile to find the photo URL
      const userProfile = await this.getUserProfile(userId);

      if (userProfile?.photoURL) {
        // SECURITY: Extract storage path from download URL
        // photoURL is a full HTTPS URL (from getDownloadURL), not a storage path
        // We need to extract the actual storage path from the URL
        const photoURL = userProfile.photoURL;
        
        // SECURITY: Validate URL format
        if (!photoURL || typeof photoURL !== "string") {
          secureLog('warn', 'Invalid photoURL format in deleteProfilePhoto', { userId });
          return; // Silently fail if URL is invalid
        }
        
        // SECURITY: Extract storage path from Firebase Storage URL
        let storagePath: string | null = null;
        try {
          if (photoURL.startsWith("https://firebasestorage.googleapis.com") || 
              photoURL.startsWith("https://firebasestorage.app")) {
            const urlObj = new URL(photoURL);
            const pathMatch = urlObj.pathname.match(/\/v0\/b\/[^/]+\/o\/(.+)/);
            if (pathMatch && pathMatch[1]) {
              storagePath = decodeURIComponent(pathMatch[1]);
            }
          } else if (photoURL.startsWith("gs://")) {
            // Handle gs:// URLs
            storagePath = photoURL.replace("gs://", "").split("/").slice(1).join("/");
          } else if (!photoURL.includes("/") && !photoURL.includes("://")) {
            // If it's already a storage path (legacy format)
            storagePath = photoURL;
          }
        } catch (error) {
          secureLog('warn', 'Failed to extract storage path from photoURL', { userId, error: sanitizeError(error) });
          return; // Silently fail if extraction fails
        }
        
        // SECURITY: Validate extracted storage path
        if (!storagePath || !isValidDocumentId(storagePath.split("/")[1] || "")) {
          secureLog('warn', 'Invalid storage path extracted from photoURL', { userId, storagePath });
          return; // Silently fail if path is invalid
        }
        
        // SECURITY: Ensure path belongs to this user (prevent path traversal)
        if (!storagePath.startsWith(`profile-photos/${userId}/`)) {
          secureLog('warn', 'Storage path does not belong to user', { userId, storagePath });
          return; // Silently fail if path doesn't match user
        }
        
        // SECURITY: Final path validation
        if (storagePath.includes("..") || storagePath.includes("//") || storagePath.length > 500) {
          secureLog('warn', 'Path traversal detected in storage path', { userId, storagePath });
          return; // Silently fail if path traversal detected
        }

        // Create storage reference from validated path
        const storageRef = ref(storage, storagePath);

        // Delete the file
        await deleteObject(storageRef);

        // Removed debug log to prevent sensitive data exposure
      }
    } catch (error) {
      secureLog('error', 'Failed to delete profile photo', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },

  // Security: Secure email update with reauth and validation
  async updateEmail(newEmail: string, currentPassword: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user');

      // Security: Validate email format
      const sanitizedEmail = sanitizeEmail(newEmail);
      if (!sanitizedEmail) {
        throw new Error('Invalid email format');
      }

      // Security: Enhanced rate limiting
      const rateLimitKey = `update_email_${user.uid}`;
      const rateLimitResult = firebaseRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many email update attempts. Please try again later.');
      }

      // Reauthenticate user
      const credential = EmailAuthProvider.credential(
        user.email!,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      // Update email
      await firebaseUpdateEmail(user, sanitizedEmail);

      // Send email verification
      await sendEmailVerification(user);

      // Update Firestore
      await this.updateUserProfile(user.uid, { email: sanitizedEmail });

    } catch (error) {
      // Security: Sanitize error messages
      throw new Error(sanitizeError(error));
    }
  },

  // Security: Secure password update with reauth and validation
  async updatePassword(newPassword: string, currentPassword: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user');

      // Security: Validate password strength using standardized validation (8 chars minimum)
      const passwordValidation = validatePassword(newPassword);
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.message || 'Password requirements not met');
      }

      // Security: Validate current password is provided
      if (!currentPassword || typeof currentPassword !== 'string') {
        throw new Error('Current password is required for security verification');
      }

      // Security: Enhanced rate limiting
      const rateLimitKey = `update_password_${user.uid}`;
      const rateLimitResult = firebaseRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many password update attempts. Please try again later.');
      }

      // Security: Reauthenticate user before password change (critical security requirement)
      const credential = EmailAuthProvider.credential(
        user.email!,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      // Update password
      await firebaseUpdatePassword(user, newPassword);
    } catch (error) {
      // Security: Sanitize error messages to prevent information disclosure
      throw new Error(sanitizeError(error));
    }
  },

  // Security: Enhanced secure phone number update
  async updatePhoneNumber(newPhoneNumber: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user');

      // Security: Enhanced validate phone number
      const phoneValidation = validateString(newPhoneNumber, 'Phone number', 1, 20);
      if (!phoneValidation.valid) {
        throw new Error(phoneValidation.message || 'Invalid phone number');
      }

      // Security: Rate limiting
      const rateLimitKey = `update_phone_${user.uid}`;
      const rateLimitResult = firebaseRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        throw new Error(rateLimitResult.message || 'Too many phone update attempts. Please try again later.');
      }

      // Removed debug log to prevent sensitive data exposure

      // TODO: Implement phone auth verification
      // This would require PhoneAuthProvider and verification flow
      // For now, just update Firestore
      await this.updateUserProfile(user.uid, { phoneNumber: newPhoneNumber });

      // Removed debug log to prevent sensitive data exposure

    } catch (error) {
      secureLog('error', 'Failed to update phone number', { error: sanitizeError(error) });
      throw new Error(sanitizeError(error));
    }
  },
};

// Order services removed

// Wishlist services - corrected according to FIRESTORE_CONNECTIONS.md
// Types for wishlist items
export interface WishlistItem {
  id?: string;
  productId: string;
  isFavorite: boolean;
  addedAt?: any; // Firestore timestamp
}
export const wishlistService = {
  // Add to wishlist (subcollection under user document)
  // Firestore rule: allow create - if authed() && (userId == uid() || isAdmin())
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async addToWishlist(
    userId: string,
    productId: string,
    productData?: {
      name: string;
      image: string;
      price: number;
      size?: string;
      color?: string;
    }
  ): Promise<string> {
    try {
      // Firestore rule compliance: verify userId matches authenticated user
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      try {
        verifyUserIdOwnership(userId, {
          action: 'add items to wishlist',
          resourceType: 'wishlist'
        });
      } catch (error: any) {
        // Convert AppError to Error for consistency with existing error handling
        throw new Error(error.message || 'Unauthorized access to wishlist');
      }
      
      const userDocRef = doc(db, "Users", userId);
      const wishlistCollectionRef = collection(userDocRef, "wishlist");

      // PERFORMANCE OPTIMIZATION: Use productId as document ID to enable direct operations
      // This eliminates the need for query-based duplicate checks and enables O(1) lookups
      // If productId is already used as document ID, we can directly check/update
      const wishlistDocRef = doc(wishlistCollectionRef, productId);
      
      // Check if document exists (single read, much faster than query)
      const docSnap = await getDoc(wishlistDocRef);
      
      if (docSnap.exists()) {
        // Product already exists in wishlist, return existing document ID
        return docSnap.id;
      }

      // Product doesn't exist, add it using productId as document ID
      // This makes future operations (remove, check) instant O(1) operations
      await setDoc(wishlistDocRef, {
        productId,
        isFavorite: true,
        addedAt: serverTimestamp(),
      });
      return wishlistDocRef.id;
    } catch (error) {
      throw error;
    }
  },

  // Remove from wishlist (subcollection under user document)
  // Firestore rule: allow delete - if authed() && (userId == uid() || isAdmin())
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async removeFromWishlist(userId: string, productId: string): Promise<void> {
    try {
      // Firestore rule compliance: verify userId matches authenticated user
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      try {
        verifyUserIdOwnership(userId, {
          action: 'remove items from wishlist',
          resourceType: 'wishlist'
        });
      } catch (error: any) {
        throw new Error(error.message || 'Unauthorized access to wishlist');
      }
      
      const userDocRef = doc(db, "Users", userId);
      const wishlistCollectionRef = collection(userDocRef, "wishlist");

      // PERFORMANCE OPTIMIZATION: Try direct delete first using productId as document ID
      // This is O(1) operation - much faster than querying
      // New items added via optimized addToWishlist use productId as document ID
      const wishlistDocRef = doc(wishlistCollectionRef, productId);
      
      // Check if document exists with productId as document ID (for new items)
      const docSnap = await getDoc(wishlistDocRef);
      if (docSnap.exists()) {
        // Document exists with productId as ID - direct delete (fastest path)
        await deleteDoc(wishlistDocRef);
        return; // Done!
      }
      
      // Fallback: Legacy items might not use productId as document ID
      // Use query-based delete for backward compatibility (only if needed)
      const q = query(
        wishlistCollectionRef,
        where("productId", "==", productId)
      );
      const querySnapshot = await getDocs(q);
      
      // Batch delete all matching documents (should typically be 0-1)
      if (!querySnapshot.empty) {
        const deletePromises = querySnapshot.docs.map((doc) =>
          deleteDoc(doc.ref)
        );
        await Promise.all(deletePromises);
      }
    } catch (error) {
      throw error;
    }
  },

  // Get user wishlist (subcollection under user document)
  // Firestore rule: allow list, get - if authed() && (userId == uid() || isAdmin())
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async getUserWishlist(userId: string): Promise<WishlistItem[]> {
    try {
      // Firestore rule compliance: verify userId matches authenticated user
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      try {
        verifyUserIdOwnership(userId, {
          action: 'view wishlist',
          resourceType: 'wishlist'
        });
      } catch (error: any) {
        throw new Error(error.message || 'Unauthorized access to wishlist');
      }
      
      const userDocRef = doc(db, "Users", userId);
      const wishlistCollectionRef = collection(userDocRef, "wishlist");

      const q = query(wishlistCollectionRef, orderBy("addedAt", "desc"));
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as WishlistItem[];
    } catch (error) {
      throw error;
    }
  },

  // Get wishlist count for a user
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async getWishlistCount(userId: string): Promise<number> {
    try {
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      try {
        verifyUserIdOwnership(userId, {
          action: 'get wishlist count',
          resourceType: 'wishlist'
        });
      } catch (error: any) {
        throw new Error(error.message || 'Unauthorized access to wishlist');
      }
      
      const userDocRef = doc(db, "Users", userId);
      const wishlistCollectionRef = collection(userDocRef, "wishlist");
      const querySnapshot = await getDocs(wishlistCollectionRef);
      return querySnapshot.size;
    } catch (error) {
      throw error;
    }
  },

  // Check if item is in wishlist (subcollection under user document)
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async isInWishlist(userId: string, productId: string): Promise<boolean> {
    try {
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      try {
        verifyUserIdOwnership(userId, {
          action: 'check wishlist',
          resourceType: 'wishlist'
        });
      } catch (error: any) {
        throw new Error(error.message || 'Unauthorized access to wishlist');
      }
      
      const userDocRef = doc(db, "Users", userId);
      const wishlistCollectionRef = collection(userDocRef, "wishlist");

      const q = query(
        wishlistCollectionRef,
        where("productId", "==", productId)
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      throw error;
    }
  },

  // Clear entire wishlist (subcollection under user document)
  // Firestore rule: allow delete - if authed() && (userId == uid() || isAdmin())
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async clearWishlist(userId: string): Promise<void> {
    try {
      // Firestore rule compliance: verify userId matches authenticated user
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      try {
        verifyUserIdOwnership(userId, {
          action: 'clear wishlist',
          resourceType: 'wishlist'
        });
      } catch (error: any) {
        throw new Error(error.message || 'Unauthorized access to wishlist');
      }
      
      const userDocRef = doc(db, "Users", userId);
      const wishlistCollectionRef = collection(userDocRef, "wishlist");

      const querySnapshot = await getDocs(wishlistCollectionRef);

      const deletePromises = querySnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);
    } catch (error) {
      throw error;
    }
  },
};

// Rewards services - corrected according to FIRESTORE_CONNECTIONS.md
export const rewardsService = {
  // Get user rewards - enhanced with multiple collection checks
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async getUserRewards(userId: string): Promise<UserRewards | null> {
    try {
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      try {
        verifyUserIdOwnership(userId, {
          action: 'view rewards',
          resourceType: 'rewards'
        });
      } catch (error: any) {
        throw new Error(error.message || 'Unauthorized access to rewards');
      }
      
      // First, try the rewards collection
      const q = query(rewardsCollection, where("userId", "==", userId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        return { id: doc.id, ...data } as UserRewards;
      }

      // If not found in rewards collection, try Users collection
      const userRef = doc(db, "Users", userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();

        // Check if user has rewards data embedded
        if (userData.points !== undefined || userData.rewards !== undefined) {
          const rewardsData = {
            userId,
            points: userData.points || userData.rewards?.points || 0,
            vouchers: userData.vouchers || userData.rewards?.vouchers || [],
            giftCards: userData.giftCards || userData.rewards?.giftCards || [],
            walletBalance:
              userData.walletBalance || userData.rewards?.walletBalance || 0,
            updatedAt: userData.updatedAt || serverTimestamp(),
          };
          return rewardsData as UserRewards;
        }
      }

      // If still not found, try other possible collection names
      const possibleCollections = [
        "userRewards",
        "loyalty",
        "points",
        "user_points",
      ];

      for (const collectionName of possibleCollections) {
        try {
          const altCollection = collection(db, collectionName);
          const altQuery = query(altCollection, where("userId", "==", userId));
          const altSnapshot = await getDocs(altQuery);

          if (!altSnapshot.empty) {
            const doc = altSnapshot.docs[0];
            const data = doc.data();
            return { id: doc.id, ...data } as UserRewards;
          }
        } catch (error) {
          // Collection doesn't exist or not accessible
        }
      }

      // Create default rewards profile
      const defaultRewards: Omit<UserRewards, "id"> = {
        userId,
        points: 0,
        vouchers: [],
        giftCards: [],
        walletBalance: 0,
        updatedAt: serverTimestamp() as any,
      };
      const docRef = await addDoc(rewardsCollection, defaultRewards);
      return { id: docRef.id, ...defaultRewards };
    } catch (error) {
      throw error;
    }
  },

  // Update user rewards
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async updateUserRewards(
    userId: string,
    rewardsData: Partial<UserRewards>
  ): Promise<void> {
    try {
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to update rewards');
      }
      
      if (userId !== currentUser.uid) {
        // SECURITY: Log unauthorized access attempt
        secureLog('warn', 'IDOR attempt detected - unauthorized rewards update', {
          requestedUserId: userId,
          currentUserId: currentUser.uid,
          action: 'updateUserRewards'
        });
        throw new Error('You can only update your own rewards');
      }
      
      const q = query(rewardsCollection, where("userId", "==", userId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // Create new rewards profile
        await addDoc(rewardsCollection, {
          userId,
          points: 0,
          vouchers: [],
          giftCards: [],
          walletBalance: 0,
          ...rewardsData,
          updatedAt: serverTimestamp() as any,
        });
      } else {
        // Update existing rewards profile
        const docRef = doc(rewardsCollection, querySnapshot.docs[0].id);
        await updateDoc(docRef, {
          ...rewardsData,
          updatedAt: serverTimestamp() as any,
        });
      }
    } catch (error) {
      throw error;
    }
  },

  // Voucher methods - integrated with coupon system
  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async getUserVouchers(userId: string): Promise<UserVoucher[]> {
    try {
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to view vouchers');
      }
      
      if (userId !== currentUser.uid) {
        // SECURITY: Log unauthorized access attempt
        secureLog('warn', 'IDOR attempt detected - unauthorized vouchers access', {
          requestedUserId: userId,
          currentUserId: currentUser.uid,
          action: 'getUserVouchers'
        });
        throw new Error('You can only view your own vouchers');
      }
      
      // Get claimed coupons from the new coupon system
      const claimedCouponsRef = collection(
        db,
        "Users",
        userId,
        "claimedCoupons"
      );
      const claimedCouponsSnap = await getDocs(claimedCouponsRef);

      const userVouchers: UserVoucher[] = [];

      for (const docSnapshot of claimedCouponsSnap.docs) {
        const claimedData = docSnapshot.data();

        // Get the actual coupon data
        const couponRef = doc(db, "coupons", docSnapshot.id);
        const couponSnap = await getDoc(couponRef);

        if (couponSnap.exists()) {
          const couponData = couponSnap.data() as any;

          // Convert coupon to voucher format for UI compatibility
          // Handle free delivery detection - check multiple possible fields
          const discountType = couponData.discountType || couponData.type;
          const couponCategory = couponData.couponCategory;
          let voucherDiscountType: "percentage" | "fixed" | "amount" | "free_delivery";
          
          if (couponCategory === "delivery" || 
              discountType === "shipping" || 
              discountType === "free_delivery" ||
              discountType === "free_shipping") {
            voucherDiscountType = "free_delivery";
          } else if (discountType === "percentage") {
            voucherDiscountType = "percentage";
          } else if (discountType === "amount" || discountType === "fixed") {
            voucherDiscountType = "amount";
          } else {
            voucherDiscountType = "fixed";
          }

          const voucher: Voucher = {
            id: docSnapshot.id,
            code: couponData.code,
            title: couponData.title || `Coupon ${couponData.code}`,
            description:
              couponData.description || `${couponData.discount}% discount`,
            discountType: voucherDiscountType,
            discountValue: couponData.discount || couponData.value || 0,
            minPurchaseAmount: couponData.minimumPurchase || couponData.minOrderAmount,
            maxDiscountAmount: couponData.maximumDiscount || couponData.maxDiscount,
            isActive: couponData.isActive !== false,
            expiresAt: couponData.endDate || couponData.endsAt,
            createdAt: couponData.createdAt,
            updatedAt: couponData.updatedAt,
            // Include fields needed for product matching
            isGlobal: couponData.isGlobal,
            sellerId: couponData.sellerId,
            applicableProducts: couponData.applicableProducts,
            startDate: couponData.startDate || couponData.startsAt,
            endDate: couponData.endDate || couponData.endsAt,
          };

          const userVoucher: UserVoucher = {
            id: docSnapshot.id,
            userId,
            voucherId: docSnapshot.id,
            voucher,
            isUsed: claimedData.used || false,
            usedAt: claimedData.usedAt,
            expiresAt: couponData.endDate,
            claimedAt: claimedData.claimedAt,
            orderId: claimedData.orderId,
            createdAt: claimedData.createdAt,
            updatedAt: claimedData.updatedAt,
          };

          userVouchers.push(userVoucher);
        }
      }

      return userVouchers;
    } catch (error) {
      secureLog('error', "Error fetching user vouchers", { error });
      return [];
    }
  },

  async getActiveVouchers(): Promise<Voucher[]> {
    try {
      // Get active global coupons that are visible
      const couponsRef = collection(db, "coupons");
      const q = query(
        couponsRef,
        where("isActive", "==", true),
        where("isGlobal", "==", true),
        where("isVisible", "==", true) // Only show visible coupons
      );
      const couponsSnap = await getDocs(q);

      const vouchers: Voucher[] = [];

      for (const docSnapshot of couponsSnap.docs) {
        const couponData = docSnapshot.data() as any;
        const now = new Date();
        const startDate =
          couponData.startDate?.toDate?.() ||
          (couponData.startDate ? new Date(couponData.startDate) : new Date(0));
        const endDate =
          couponData.endDate?.toDate?.() ||
          (couponData.endDate
            ? new Date(couponData.endDate)
            : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)); // Default to 1 year from now

        // Check if coupon is currently valid
        if (now >= startDate && now <= endDate) {
          const voucher: Voucher = {
            id: docSnapshot.id,
            code: couponData.code,
            title: couponData.title || `Coupon ${couponData.code}`,
            description:
              couponData.description || `${couponData.discount}% discount`,
            discountType:
              // Check for free delivery: couponCategory === 'delivery' OR discountType === 'shipping' OR discountType === 'free_delivery'
              couponData.couponCategory === "delivery" ||
              couponData.discountType === "shipping" ||
              couponData.discountType === "free_delivery"
                ? "free_delivery"
                : couponData.discountType === "percentage"
                ? "percentage"
                : couponData.discountType === "amount"
                ? "amount"
                : "fixed",
            discountValue: couponData.discount,
            minPurchaseAmount: couponData.minimumPurchase,
            maxDiscountAmount: couponData.maximumDiscount,
            isActive: couponData.isActive,
            expiresAt: couponData.endDate,
            createdAt: couponData.createdAt,
            updatedAt: couponData.updatedAt,
            isVisible: couponData.isVisible !== false, // Default to true for backward compatibility
          };

          // Only include claimable vouchers (if isClaimable field exists and is true)
          // If isClaimable is not set, include all vouchers for backward compatibility
          if (couponData.isClaimable === undefined || couponData.isClaimable === true) {
            vouchers.push(voucher);
          }
        }
      }

      return vouchers;
    } catch (error) {
      secureLog('error', "Error fetching active vouchers", { error });
      return [];
    }
  },

  // SECURITY: IDOR Protection - Validates userId matches authenticated user
  async claimVoucher(userId: string, voucherId: string): Promise<void> {
    try {
      // SECURITY: IDOR Protection - Verify userId matches authenticated user
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to claim vouchers');
      }
      
      if (userId !== currentUser.uid) {
        // SECURITY: Log unauthorized access attempt
        secureLog('warn', 'IDOR attempt detected - unauthorized voucher claim', {
          requestedUserId: userId,
          currentUserId: currentUser.uid,
          action: 'claimVoucher',
          voucherId
        });
        throw new Error('You can only claim vouchers for your own account');
      }
      
      // Get the coupon data
      const couponRef = doc(db, "coupons", voucherId);
      const couponSnap = await getDoc(couponRef);

      if (!couponSnap.exists()) {
        throw new Error("Voucher not found");
      }

      // Check if user has already claimed this coupon
      const claimedCouponRef = doc(
        db,
        "Users",
        userId,
        "claimedCoupons",
        voucherId
      );
      const claimedSnap = await getDoc(claimedCouponRef);

      if (claimedSnap.exists()) {
        throw new Error("Voucher already claimed");
      }

      // Claim the voucher by adding to user's claimedCoupons
      await setDoc(claimedCouponRef, {
        claimedAt: serverTimestamp(),
        used: false,
        expired: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  },

  async createSampleVouchers(): Promise<void> {
    try {
      // Create sample coupons in the coupons collection
      const sampleCoupons = [
        {
          code: "WELCOME10",
          title: "Welcome Discount",
          description: "Get 10% off on your first purchase",
          discountType: "percentage",
          discount: 10,
          minimumPurchase: 500,
          maximumDiscount: 1000,
          isActive: true,
          isGlobal: true,
          startDate: serverTimestamp(),
          endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
          usedCount: 0,
          maxUsePerUser: 1,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        {
          code: "FREESHIP",
          title: "Free Shipping",
          description: "Free shipping on orders above ₹1000",
          discountType: "free_delivery",
          discount: 0,
          minimumPurchase: 1000,
          isActive: true,
          isGlobal: true,
          startDate: serverTimestamp(),
          endDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000), // 60 days from now
          usedCount: 0,
          maxUsePerUser: 2,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        {
          code: "FLAT50",
          title: "Flat ₹50 Off",
          description: "Get ₹50 off on orders above ₹200",
          discountType: "amount",
          discount: 50,
          minimumPurchase: 200,
          isActive: true,
          isGlobal: true,
          startDate: serverTimestamp(),
          endDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000), // 45 days from now
          usedCount: 0,
          maxUsePerUser: 1,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
      ];

      const batch = writeBatch(db);

      for (const couponData of sampleCoupons) {
        const couponRef = doc(collection(db, "coupons"), couponData.code);
        batch.set(couponRef, couponData);
      }

      await batch.commit();
    } catch (error) {
      throw error;
    }
  },
};

/**
 * Messaging path standardization:
 * - WRITE path: conversations/{conversationId}/messages
 * - READ path: prefer nested; also merge legacy top-level `messages` with conversationId
 * - Transactional updates to conversation on send & mark-read
 * - Typing status stored as `typingUserIds: string[]` on conversation
 */

// Messaging service helpers
const currentUidOrNull = (): string | null => {
  return auth.currentUser?.uid || null;
};

const convRef = (id: string) => doc(db, "conversations", id);
const msgsCol = (id: string) => collection(db, "conversations", id, "messages");

const safeServerTimestamp = () => serverTimestamp() as any;

// Basic retry transaction helper
const withRetryTransaction = async <T>(
  fn: (transaction: any) => Promise<T>
): Promise<T> => {
  const { runTransaction } = await import("firebase/firestore");
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return await runTransaction(db, fn);
    } catch (error) {
      lastError = error as Error;
      if (attempt === 2) throw lastError;
      // Wait before retry (exponential backoff)
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, attempt) * 100)
      );
    }
  }
  throw lastError;
};

// Performance: Simple cache utility
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function getCachedData(key: string) {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached;
  }
  cache.delete(key);
  return null;
}

function setCachedData(key: string, data: any) {
  cache.set(key, { data, timestamp: Date.now() });
  
  // Performance: Clean up old cache entries
  if (cache.size > 100) {
    const entries = Array.from(cache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    const toDelete = entries.slice(0, 20);
    toDelete.forEach(([key]) => cache.delete(key));
  }
}

// Basic messaging services (without FCM)
export const messagingService = {
  // Performance: Cache utility methods
  getCachedData,
  setCachedData,
  // Security: Enhanced user profile fetching with validation and sanitization
  async getUserProfile(uid: string): Promise<UserModel | null> {
    try {
      // Security: Validate user ID
      if (!uid || !isValidDocumentId(uid)) {
        return null;
      }

      // Security: Rate limiting
      const rateLimitKey = `get_user_profile_${uid}`;
      if (!firebaseRateLimiter.isAllowed(rateLimitKey)) {
        return null;
      }

      const userRef = doc(db, 'Users', uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        return null;
      }

      const userData = userSnap.data();
      
      // Security: Sanitize user profile data
      const userProfile: UserModel = {
        id: uid,
        uid: uid,
        firstName: sanitizeString(userData.firstName, 50) || '',
        lastName: sanitizeString(userData.lastName, 50) || '',
        displayName: sanitizeString(userData.displayName || userData.shopName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email?.split('@')[0] || 'User', 100) || 'User',
        email: sanitizeEmail(userData.email) || '',
        phoneNumber: sanitizeString(userData.phoneNumber, 20) || '',
        photoURL: sanitizeString(userData.photoURL, 500) || '',
        role: ['user', 'seller', 'admin'].includes(userData.role) ? userData.role : 'user',
        createdAt: userData.createdAt,
        updatedAt: userData.updatedAt,
      };

      return userProfile;
    } catch (error) {
      // Security: Sanitize error logging
      secureLog('warn', 'Error fetching user profile');
      return null;
    }
  },

  // Security: Enhanced batch user profiles with validation and sanitization
  async getBatchUserProfiles(uids: string[]): Promise<Map<string, UserModel>> {
    try {
      if (!uids.length) {
        return new Map();
      }

      // Security: Validate and filter UIDs
      const validUids = uids.filter(uid => isValidDocumentId(uid));
      if (validUids.length === 0) {
        return new Map();
      }

      const uniqueUids = Array.from(new Set(validUids));
      const userProfiles = new Map<string, UserModel>();

      // Security: Rate limiting
      const rateLimitKey = 'get_batch_user_profiles';
      if (!firebaseRateLimiter.isAllowed(rateLimitKey)) {
        return new Map();
      }

      // Fetch in batches of 10 (Firestore limit)
      const batchSize = 10;
      for (let i = 0; i < uniqueUids.length; i += batchSize) {
        const batch = uniqueUids.slice(i, i + batchSize);
        const refs = batch.map((uid) => doc(db, "Users", uid));

        try {
          const snapshots = await Promise.all(refs.map((ref) => getDoc(ref)));

          snapshots.forEach((snap, index) => {
            const uid = batch[index];
            if (snap.exists()) {
              const userData = snap.data();
              
              // Security: Sanitize user profile data
              const userProfile: UserModel = {
                id: uid,
                uid: uid,
                firstName: sanitizeString(userData.firstName, 50) || '',
                lastName: sanitizeString(userData.lastName, 50) || '',
                displayName: sanitizeString(userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email?.split('@')[0] || 'User', 100) || 'User',
                email: sanitizeEmail(userData.email) || '',
                phoneNumber: sanitizeString(userData.phoneNumber, 20) || '',
                photoURL: sanitizeString(userData.photoURL, 500) || '',
                role: ['user', 'seller', 'admin'].includes(userData.role) ? userData.role : 'user',
                createdAt: userData.createdAt,
                updatedAt: userData.updatedAt,
              };
              userProfiles.set(uid, userProfile);
            } else {
              // Security: Create a fallback profile for missing users
              const fallbackProfile: UserModel = {
                id: uid,
                uid: uid,
                firstName: "Unknown",
                lastName: "User",
                displayName: "Unknown User",
                email: "",
                phoneNumber: "",
                photoURL: "",
                role: "user",
                createdAt: undefined,
                updatedAt: undefined,
              };
              userProfiles.set(uid, fallbackProfile);
            }
          });
        } catch (batchError) {
          // Security: Sanitize error logging
          secureLog('warn', 'Error fetching batch of user profiles');
          // Create fallback profiles for the entire batch
          batch.forEach((uid) => {
            const fallbackProfile: UserModel = {
              id: uid,
              uid: uid,
              firstName: "Unknown",
              lastName: "User",
              displayName: "Unknown User",
              email: "",
              phoneNumber: "",
              photoURL: "",
              role: "user",
              createdAt: undefined,
              updatedAt: undefined,
            };
            userProfiles.set(uid, fallbackProfile);
          });
        }
      }

      return userProfiles;
    } catch (error) {
      // Security: Sanitize error logging
      secureLog('error', 'Critical error in getBatchUserProfiles');
      // Return fallback profiles for all UIDs
      const fallbackProfiles = new Map<string, UserModel>();
      uids.forEach(uid => {
        if (isValidDocumentId(uid)) {
          fallbackProfiles.set(uid, {
            id: uid,
            uid: uid,
            firstName: 'Unknown',
            lastName: 'User',
            displayName: 'Unknown User',
            email: '',
            phoneNumber: '',
            photoURL: '',
            role: 'user',
            createdAt: undefined,
            updatedAt: undefined
          });
        }
      });
      return fallbackProfiles;
    }
  },

  // Performance: Optimized conversations fetching with caching
  async getConversationsByUser(): Promise<Conversation[]> {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      // Removed debug log to prevent excessive console output
      return [];
    }

    // Performance: Check cache first
    const cacheKey = `conversations_${userId}`;
    const cached = this.getCachedData(cacheKey);
    if (cached && Date.now() - cached.timestamp < 30000) { // 30 second cache
      // Removed debug log to prevent excessive console output
      return cached.data;
    }

    try {
      // Removed debug log to prevent sensitive data exposure (userId)
      const conversationsRef = collection(db, 'conversations');
      
      // Performance: Use single optimized query with proper index
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', userId),
        orderBy('updatedAt', 'desc'),
        limit(50) // Performance: Limit results for better performance
      );
      
      const querySnapshot = await getDocs(q);
      // Removed debug logs to prevent excessive console output
      
      const conversations: Conversation[] = [];
      const userIds = new Set<string>();
      
      // Performance: Process conversations and collect user IDs
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        conversations.push({ id: doc.id, ...data } as Conversation);
        
        // Collect unique user IDs for batch profile fetching
        if (data.participants && Array.isArray(data.participants)) {
          data.participants.forEach((uid: string) => {
            if (uid !== userId && isValidDocumentId(uid)) {
              userIds.add(uid);
            }
          });
        }
      });

      // Performance: Batch fetch user profiles
      const userProfiles = await this.getBatchUserProfiles(Array.from(userIds));
      
      // Performance: Enrich conversations with user profiles
      const enrichedConversations = conversations.map(conv => {
        const otherUserId = conv.participants?.find(uid => uid !== userId);
        if (otherUserId && userProfiles.has(otherUserId)) {
          return { ...conv, otherUser: userProfiles.get(otherUserId) };
        }
        return conv;
      });

      // Performance: Cache the results
      setCachedData(cacheKey, enrichedConversations);
      
      return enrichedConversations;
    } catch (error) {
      secureLog('error', "Failed to get conversations", { error });
      return [];
    }
  },

  // Get or create conversation with proper validation
  async getOrCreateConversation(otherUserId: string): Promise<Conversation> {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      throw new Error("User must be authenticated to create conversations");
    }

    if (!otherUserId || otherUserId === userId) {
      throw new Error("Invalid other user ID");
    }

    // Check if conversation already exists
    const existingConversation = await this.getConversationByParticipants([
      userId,
      otherUserId,
    ]);
    if (existingConversation) {
      return existingConversation;
    }

    // Create new conversation
    const conversationData = {
      participants: [userId, otherUserId],
      lastMessage: "",
      lastMessageTime: safeServerTimestamp(),
      lastMessageSenderId: "",
      unreadCounts: {
        [userId]: 0,
        [otherUserId]: 0,
      },
      typingUserIds: [],
      blockedBy: [],
      mutedBy: [],
      createdAt: safeServerTimestamp(),
      updatedAt: safeServerTimestamp(),
    };

    const conversationRef = await addDoc(
      collection(db, "conversations"),
      conversationData
    );

    const conversation: Conversation = {
      id: conversationRef.id,
      participants: conversationData.participants,
      lastMessage: conversationData.lastMessage,
      lastMessageSenderId: conversationData.lastMessageSenderId,
      lastMessageTime: conversationData.lastMessageTime,
      unreadCount: 0,
      unreadCounts: conversationData.unreadCounts,
      typingUserIds: conversationData.typingUserIds,
      blockedBy: conversationData.blockedBy,
      mutedBy: conversationData.mutedBy,
      createdAt: conversationData.createdAt,
      updatedAt: conversationData.updatedAt,
    };

    // Add user profile for the other participant
    const userProfile = await this.getUserProfile(otherUserId);
    if (userProfile) {
      conversation.otherUser = userProfile;
    }

    return conversation;
  },

  // Clean up duplicate conversations in the database
  async cleanupDuplicateConversations(): Promise<void> {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      throw new Error('User must be authenticated to cleanup conversations');
    }

    try {
      // Removed debug log to prevent excessive console output
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', userId)
      );
      
      const querySnapshot = await getDocs(q);
      const conversations = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as any));

      // Group conversations by participant pairs
      const conversationGroups = new Map<string, any[]>();
      conversations.forEach(conv => {
        if (conv.participants && conv.participants.length === 2) {
          const sortedParticipants = [...conv.participants].sort();
          const key = sortedParticipants.join('_');
          
          if (!conversationGroups.has(key)) {
            conversationGroups.set(key, []);
          }
          conversationGroups.get(key)!.push(conv);
        }
      });

      // Find and remove duplicates
      const batch = writeBatch(db);
      let duplicatesRemoved = 0;
      
      // Check if we have any duplicates to process
      let hasDuplicates = false;

      conversationGroups.forEach((group, key) => {
        if (group.length > 1) {
          hasDuplicates = true;
          // Removed debug log to prevent sensitive data exposure (participants may contain user IDs)
          
          // Sort by updatedAt to keep the most recent one
          group.sort((a, b) => {
            const aTime = a.updatedAt || a.lastMessageTime;
            const bTime = b.updatedAt || b.lastMessageTime;
            if (aTime && bTime) {
              const aMillis = typeof aTime === 'object' && 'toMillis' in aTime ? aTime.toMillis() : aTime;
              const bMillis = typeof bTime === 'object' && 'toMillis' in bTime ? bTime.toMillis() : bTime;
              
              // Ensure we have valid millisecond values
              if (typeof aMillis === 'number' && typeof bMillis === 'number') {
                return bMillis - aMillis;
              }
            }
            return 0;
          });

          // Keep the first (most recent) conversation, delete the rest
          const toKeep = group[0];
          const toDelete = group.slice(1);
          
          // Removed debug logs to prevent sensitive data exposure (conversation IDs)
          toDelete.forEach(conv => {
            // Removed debug log to prevent sensitive data exposure
            batch.delete(doc(db, 'conversations', conv.id));
            duplicatesRemoved++;
          });
        }
      });

      if (hasDuplicates && duplicatesRemoved > 0) {
        await batch.commit();
        // Removed debug log to prevent excessive console output
      } else if (hasDuplicates) {
        // Removed debug log to prevent excessive console output
      } else {
        // Removed debug log to prevent excessive console output
      }
    } catch (error) {
      secureLog('error', 'Failed to cleanup duplicate conversations', { error });
      throw error;
    }
  },

  // REMOVED: createConversation wrapper - use getOrCreateConversation directly

  // Enhanced conversation fetching by participants with proper deduplication
  async getConversationByParticipants(participants: string[]): Promise<Conversation | null> {
    try {
      if (participants.length !== 2) {
        return null;
      }

      const conversationsRef = collection(db, 'conversations');
      // Query for conversations containing the first participant
      const q1 = query(
        conversationsRef,
        where("participants", "array-contains", participants[0])
      );
      
      const querySnapshot1 = await getDocs(q1);
      
      // Check each conversation to see if it contains both participants
      for (const doc of querySnapshot1.docs) {
        const data = doc.data();
        if (
          data.participants &&
          data.participants.length === 2 &&
          data.participants.includes(participants[0]) &&
          data.participants.includes(participants[1])
        ) {
          const conversation: Conversation = {
            id: doc.id,
            participants: data.participants,
            lastMessage: data.lastMessage || '',
            lastMessageSenderId: data.lastMessageSenderId || '',
            lastMessageTime: data.lastMessageTime || safeServerTimestamp(),
            unreadCount: data.unreadCount || 0,
            unreadCounts: data.unreadCounts || {},
            blockedBy: data.blockedBy || [],
            mutedBy: data.mutedBy || [],
            createdAt: data.createdAt || safeServerTimestamp(),
            updatedAt: data.updatedAt || safeServerTimestamp()
          };

          // Add user profile for the other participant
          const otherUserId = conversation.participants.find(uid => uid !== participants[0]);
          if (otherUserId) {
            const userProfile = await this.getUserProfile(otherUserId);
            if (userProfile) {
              conversation.otherUser = userProfile;
            }
          }

          return conversation;
        }
      }
      
      // If not found with first participant, try with second participant
      const q2 = query(
        conversationsRef,
        where('participants', 'array-contains', participants[1])
      );
      
      const querySnapshot2 = await getDocs(q2);
      
      for (const doc of querySnapshot2.docs) {
        const data = doc.data();
        if (data.participants && 
            data.participants.length === 2 && 
            data.participants.includes(participants[0]) && 
            data.participants.includes(participants[1])) {
          
          const conversation: Conversation = {
            id: doc.id,
            participants: data.participants,
            lastMessage: data.lastMessage || '',
            lastMessageSenderId: data.lastMessageSenderId || '',
            lastMessageTime: data.lastMessageTime || safeServerTimestamp(),
            unreadCount: data.unreadCount || 0,
            unreadCounts: data.unreadCounts || {},
            blockedBy: data.blockedBy || [],
            mutedBy: data.mutedBy || [],
            createdAt: data.createdAt || safeServerTimestamp(),
            updatedAt: data.updatedAt || safeServerTimestamp()
          };

          // Add user profile for the other participant
          const otherUserId = conversation.participants.find(uid => uid !== participants[1]);
          if (otherUserId) {
            const userProfile = await this.getUserProfile(otherUserId);
            if (userProfile) {
              conversation.otherUser = userProfile;
            }
          }

          return conversation;
        }
      }

      return null;
    } catch (error) {
      secureLog('error', "Failed to get conversation by participants", { error });
      return null;
    }
  },

  // Refresh user profile for a specific conversation
  async refreshConversationUserProfile(
    conversationId: string
  ): Promise<Conversation | null> {
    try {
      const conversation = await this.getConversationById(conversationId);
      if (!conversation) return null;

      const userId = currentUidOrNull();
      if (!userId) return null;
      const otherUserId = conversation.participants.find(
        (uid) => uid !== userId
      );

      if (otherUserId) {
        const userProfile = await this.getUserProfile(otherUserId);
        if (userProfile) {
          conversation.otherUser = userProfile;
          // Refreshed user profile for conversation
        } else {
          // SECURITY: Removed console.warn to prevent information leakage
        }
      }

      return conversation;
    } catch (error) {
      secureLog('error', "Error refreshing conversation user profile", { error });
      return null;
    }
  },

  // Enhanced conversation fetching by ID
  async getConversationById(
    conversationId: string
  ): Promise<Conversation | null> {
    try {
      const conversationRef = doc(db, "conversations", conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        return null;
      }

      const data = conversationDoc.data();
      const conversation: Conversation = {
        id: conversationDoc.id,
        participants: data.participants || [],
        lastMessage: data.lastMessage || "",
        lastMessageSenderId: data.lastMessageSenderId || "",
        lastMessageTime: data.lastMessageTime || (serverTimestamp() as any),
        unreadCount: data.unreadCount || 0,
        unreadCounts: data.unreadCounts || {},
        blockedBy: data.blockedBy || [],
        mutedBy: data.mutedBy || [],
        createdAt: data.createdAt || (serverTimestamp() as any),
        updatedAt: data.updatedAt || (serverTimestamp() as any),
      };

      // Add user profile for the other participant
      const userId = uidOrThrow();
      if (userId) {
        const otherUserId = conversation.participants.find(
          (uid) => uid !== userId
        );
        if (otherUserId) {
          const userProfile = await this.getUserProfile(otherUserId);
          if (userProfile) {
            conversation.otherUser = userProfile;
          }
        }
      }

      return conversation;
    } catch (error) {
      secureLog('error', "Failed to get conversation by ID", { error });
      return null;
    }
  },

  // Enhanced message sending with transactional updates and file attachments
  async sendMessage(
    conversationId: string,
    messageText: string,
    opts?: { file?: File; image?: File }
  ): Promise<Message | null> {
    const userId = currentUidOrNull();
    if (!userId) {
      throw new Error("User must be authenticated to send messages");
    }

    if (!messageText.trim() && !opts?.file && !opts?.image) {
      throw new Error("Message must contain text or a file");
    }

    if (messageText.length > 1000) {
      throw new Error("Message text must be less than 1000 characters");
    }

    // Handle file upload if present
    let imageUrl: string | undefined;
    let fileUrl: string | undefined;
    let lastMessageType: "text" | "image" | "file" = "text";
    let lastMessage = messageText.trim();

    if (opts?.file || opts?.image) {
      const file = opts.image || opts.file!;
      // Client-side safety: enforce basic MIME and size validation
      const MAX_IMG = 5 * 1024 * 1024; // 5MB
      const MAX_DOC = 10 * 1024 * 1024; // 10MB
      const ALLOWED = new Set([
        "image/png",
        "image/jpeg",
        "image/webp",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ]);
      if (!ALLOWED.has(file.type)) {
        throw new Error("Unsupported file type");
      }
      const max = file.type.startsWith("image/") ? MAX_IMG : MAX_DOC;
      if (file.size > max) {
        throw new Error("File too large");
      }
      // SECURITY: Sanitize file name to prevent path traversal attacks
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100);
      const timestamp = Date.now();
      const fileName = `${timestamp}__${sanitizedFileName}`;
      // SECURITY: Validate conversationId to prevent path traversal
      if (!conversationId || !isValidDocumentId(conversationId)) {
        throw new Error('Invalid conversation ID');
      }
      // SECURITY: Construct safe storage path with validation
      const storagePath = `conversations/${conversationId}/attachments/${fileName}`;
      
      // SECURITY: Final validation of constructed path
      if (storagePath.includes("..") || storagePath.includes("//") || storagePath.length > 500) {
        throw new Error('Invalid storage path');
      }

      try {
        const storageRef = ref(storage, storagePath);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        if (file.type.startsWith("image/")) {
          imageUrl = downloadURL;
          lastMessageType = "image";
          lastMessage = "📷 Photo";
        } else {
          fileUrl = downloadURL;
          lastMessageType = "file";
          lastMessage = "📎 Attachment";
        }
      } catch (error) {
        secureLog('error', "Failed to upload file", { error });
        throw new Error("Failed to upload file");
      }
    }

    return await withRetryTransaction(async (transaction) => {
      // Read conversation to validate and get other user
      const conversationRef = convRef(conversationId);
      const conversationDoc = await transaction.get(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error("Conversation not found");
      }

      const conversationData = conversationDoc.data();
      if (!conversationData.participants.includes(userId)) {
        throw new Error("User is not a participant in this conversation");
      }

      // Compute other user ID
      const otherUserId = conversationData.participants.find(
        (uid: string) => uid !== userId
      );
      if (!otherUserId) {
        throw new Error("Invalid conversation participants");
      }

      // Create message in nested path
      const messageRef = doc(msgsCol(conversationId));
      
      // Determine message type based on content
      let messageType: "text" | "image" | "file" | "voice" = "text";
      if (imageUrl) {
        messageType = "image";
      } else if (fileUrl) {
        messageType = "file";
      }
      
      const messageData = {
        id: messageRef.id,
        senderId: userId,
        receiverId: otherUserId, // Required by Firestore rules
        type: messageType, // Required by Firestore rules
        text: messageText.trim() || "",
        imageUrl: imageUrl || null,
        fileUrl: fileUrl || null,
        timestamp: safeServerTimestamp(),
        readBy: [userId],
        createdAt: safeServerTimestamp(),
        updatedAt: safeServerTimestamp(),
      };

      transaction.set(messageRef, messageData);

      // Update conversation with transactional updates
      const unreadCounts = conversationData.unreadCounts || {};
      unreadCounts[otherUserId] = (unreadCounts[otherUserId] || 0) + 1;
      unreadCounts[userId] = unreadCounts[userId] || 0; // Ensure current user has count

      transaction.update(conversationRef, {
        lastMessage,
        lastMessageType,
        lastMessageTime: safeServerTimestamp(),
        lastMessageSenderId: userId,
        unreadCounts,
        updatedAt: safeServerTimestamp(),
      });

      return {
        id: messageRef.id,
        conversationId,
        senderId: userId,
        text: messageText.trim(),
        imageUrl: imageUrl || null,
        fileUrl: fileUrl || null,
        timestamp: messageData.timestamp,
        readBy: [userId],
        createdAt: messageData.createdAt,
        updatedAt: messageData.updatedAt,
        isRead: true, // Sender has read their own message
      } as Message;
    });
  },

  // Get messages with nested path support and legacy fallback
  async getMessages(
    conversationId: string,
    messageLimit = 30,
    before?: Timestamp | null
  ): Promise<Message[]> {
    const userId = currentUidOrNull();
    if (!userId) {
      // Removed debug log to prevent excessive console output
      return [];
    }

    try {
      // First try nested path: conversations/{conversationId}/messages
      let messages: Message[] = [];

      try {
        const messagesRef = msgsCol(conversationId);

        // NOTE: No composite index needed - single field orderBy on subcollection
        let q = query(
          messagesRef,
          orderBy("timestamp", "desc"),
          limit(messageLimit)
        );

        if (before) {
          q = query(q, startAfter(before));
        }

        const querySnapshot = await getDocs(q);

        messages = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            conversationId,
            senderId: data.senderId,
            text: data.text,
            imageUrl: data.imageUrl,
            fileUrl: data.fileUrl,
            timestamp: data.timestamp,
            readBy: data.readBy || [],
            isRead: data.readBy?.includes(userId) || false,
          } as Message;
        });
      } catch (error) {
        secureLog('warn', "Failed to fetch from nested path, trying legacy", { error });
      }

      // If nested path is empty, try legacy top-level messages
      if (messages.length === 0) {
        try {
          const legacyMessagesRef = collection(db, "messages");

          // NOTE: Composite index required: messages [conversationId: asc] + [timestamp: desc]
          // Covered in firestore.indexes.json
          let q = query(
            legacyMessagesRef,
            where("conversationId", "==", conversationId),
            orderBy("timestamp", "desc"),
            limit(messageLimit)
          );

          if (before) {
            q = query(q, startAfter(before));
          }

          const querySnapshot = await getDocs(q);

          messages = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              conversationId: data.conversationId,
              senderId: data.senderId,
              text: data.text,
              imageUrl: data.imageUrl,
              fileUrl: data.fileUrl,
              timestamp: data.timestamp,
              readBy: data.readBy || [],
              isRead: data.readBy?.includes(userId) || false,
            } as Message;
          });
        } catch (error) {
          secureLog('warn', "Failed to fetch from legacy path", { error });
        }
      }

      return messages.reverse(); // Return in ascending order for UI
    } catch (error) {
      secureLog('error', "Failed to get messages", { error });
      return [];
    }
  },

  // REMOVED: getMessagesByConversation wrapper - use getMessages directly

  // Mark all messages as read with transactional updates
  async markAllAsRead(conversationId: string): Promise<void> {
    const userId = currentUidOrNull();
    if (!userId) {
      throw new Error("User must be authenticated to mark messages as read");
    }

    return await withRetryTransaction(async (transaction) => {
      // Read conversation to validate
      const conversationRef = convRef(conversationId);
      const conversationDoc = await transaction.get(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error("Conversation not found");
      }

      const conversationData = conversationDoc.data();
      if (!conversationData.participants.includes(userId)) {
        throw new Error("User is not a participant in this conversation");
      }

      // Update unreadCounts for current user
      const unreadCounts = conversationData.unreadCounts || {};
      unreadCounts[userId] = 0;

      transaction.update(conversationRef, {
        unreadCounts,
        updatedAt: safeServerTimestamp(),
      });
    });
  },

  // REMOVED: markMessagesAsRead wrapper - use markAllAsRead directly

  // Set typing status
  async setTyping(conversationId: string, isTyping: boolean): Promise<void> {
    const userId = currentUidOrNull();
    if (!userId) {
      throw new Error("User must be authenticated to set typing status");
    }

    const conversationRef = convRef(conversationId);

    if (isTyping) {
      await updateDoc(conversationRef, {
        typingUserIds: arrayUnion(userId),
        updatedAt: safeServerTimestamp(),
      });
    } else {
      await updateDoc(conversationRef, {
        typingUserIds: arrayRemove(userId),
        updatedAt: safeServerTimestamp(),
      });
    }
  },

  // Block user
  async blockUser(
    conversationId: string,
    blockedUserId: string
  ): Promise<void> {
    const userId = currentUidOrNull();
    if (!userId) {
      throw new Error("User must be authenticated to block users");
    }

    const conversationRef = convRef(conversationId);
    const conversationDoc = await getDoc(conversationRef);

    if (!conversationDoc.exists()) {
      throw new Error("Conversation not found");
    }

    const conversationData = conversationDoc.data();
    if (!conversationData.participants.includes(userId)) {
      throw new Error("User is not a participant in this conversation");
    }

    // Add the blocked user to the blockedBy array
    await updateDoc(conversationRef, {
      blockedBy: arrayUnion(blockedUserId),
      updatedAt: safeServerTimestamp(),
    });

    // Also add to user's blocked list
    const userRef = doc(db, "Users", userId);
    await updateDoc(userRef, {
      blockedUsers: arrayUnion(blockedUserId),
      updatedAt: safeServerTimestamp(),
    });
  },

  // Unblock user
  async unblockUser(
    conversationId: string,
    unblockedUserId: string
  ): Promise<void> {
    const userId = currentUidOrNull();
    if (!userId) {
      throw new Error("User must be authenticated to unblock users");
    }

    const conversationRef = convRef(conversationId);
    const conversationDoc = await getDoc(conversationRef);

    if (!conversationDoc.exists()) {
      throw new Error("Conversation not found");
    }

    const conversationData = conversationDoc.data();
    if (!conversationData.participants.includes(userId)) {
      throw new Error("User is not a participant in this conversation");
    }

    // Remove the unblocked user from the blockedBy array
    await updateDoc(conversationRef, {
      blockedBy: arrayRemove(unblockedUserId),
      updatedAt: safeServerTimestamp(),
    });

    // Also remove from user's blocked list
    const userRef = doc(db, "Users", userId);
    await updateDoc(userRef, {
      blockedUsers: arrayRemove(unblockedUserId),
      updatedAt: safeServerTimestamp(),
    });
  },

  // Mute conversation
  async muteConversation(
    conversationId: string,
    userId: string
  ): Promise<void> {
    const currentUserId = currentUidOrNull();
    if (!currentUserId) {
      throw new Error("User must be authenticated to mute conversations");
    }

    if (currentUserId !== userId) {
      throw new Error("User can only mute conversations for themselves");
    }

    const conversationRef = convRef(conversationId);
    const conversationDoc = await getDoc(conversationRef);

    if (!conversationDoc.exists()) {
      throw new Error("Conversation not found");
    }

    const conversationData = conversationDoc.data();
    if (!conversationData.participants.includes(userId)) {
      throw new Error("User is not a participant in this conversation");
    }

    await updateDoc(conversationRef, {
      mutedBy: arrayUnion(userId),
      updatedAt: safeServerTimestamp(),
    });
  },

  // Enhanced conversation muting

  // Subscribe to user presence
  async subscribeToUserPresence(
    userId: string,
    callback: (presence: {
      isOnline: boolean;
      lastSeen: Timestamp | null;
    }) => void
  ): Promise<() => void> {
    try {
      const userRef = doc(db, "Users", userId);
      const unsubscribe = onSnapshot(userRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const presence = {
            isOnline: data.isOnline || false,
            lastSeen: data.lastSeen || null,
          };
          callback(presence);
        }
      });
      return unsubscribe;
    } catch (error) {
      secureLog('error', "Failed to subscribe to user presence", { error });
      return () => {};
    }
  },

  // Delete conversation
  async deleteConversation(
    conversationId: string,
    userId: string
  ): Promise<void> {
    try {
      const conversationRef = doc(db, "conversations", conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        return;
      }

      const data = conversationDoc.data();
      if (!data.participants.includes(userId)) {
        throw new Error("User not authorized to delete this conversation");
      }

      // Delete all messages in the conversation (both legacy top-level and nested collection)
      const batch = writeBatch(db);
      // Legacy top-level messages
      {
        const messagesRef = collection(db, "messages");
        const qLegacy = query(
          messagesRef,
          where("conversationId", "==", conversationId)
        );
        const messagesSnapshot = await getDocs(qLegacy);
        messagesSnapshot.docs.forEach((docSnap) => {
          batch.delete(docSnap.ref);
        });
      }
      // Nested subcollection messages: conversations/{id}/messages
      {
        const nestedMessagesRef = msgsCol(conversationId);
        const nestedSnapshot = await getDocs(nestedMessagesRef);
        nestedSnapshot.docs.forEach((docSnap) => {
          batch.delete(docSnap.ref);
        });
      }
      // Delete the conversation doc
      batch.delete(conversationRef);
      await batch.commit();
    } catch (error) {
      secureLog('error', "Failed to delete conversation", { error });
      throw error;
    }
  },

  // Enhanced conversation unmuting
  async unmuteConversation(
    conversationId: string,
    userId: string
  ): Promise<void> {
    try {
      const conversationRef = doc(db, "conversations", conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error("Conversation not found");
      }

      const conversationData = conversationDoc.data();

      // Check if user is a participant
      if (!conversationData.participants.includes(userId)) {
        throw new Error("User is not a participant in this conversation");
      }

      // Remove user from mutedBy array
      const mutedBy = conversationData.mutedBy || [];
      if (mutedBy.includes(userId)) {
        await updateDoc(conversationRef, {
          mutedBy: arrayRemove(userId),
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      throw error;
    }
  },

  // Report user in conversation
  async reportUser(
    conversationId: string,
    reportedUserId: string,
    reason: string,
    description?: string
  ): Promise<void> {
    const currentUserId = currentUidOrNull();
    if (!currentUserId) {
      throw new Error("User must be authenticated to report users");
    }

    try {
      const conversationRef = doc(db, "conversations", conversationId);
      const conversationDoc = await getDoc(conversationRef);

      if (!conversationDoc.exists()) {
        throw new Error("Conversation not found");
      }

      const conversationData = conversationDoc.data();
      if (!conversationData.participants.includes(currentUserId)) {
        throw new Error("User is not a participant in this conversation");
      }

      // Update conversation with report information
      await updateDoc(conversationRef, {
        reportedBy: arrayUnion(currentUserId),
        reportReason: reason,
        reportDescription: description,
        updatedAt: serverTimestamp(),
      });

      // Create a user report document
      const reportRef = doc(collection(db, "userReports"));
      await setDoc(reportRef, {
        id: reportRef.id,
        conversationId,
        reporterId: currentUserId,
        reportedUserId,
        reason,
        description: description || "",
        status: "pending",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      secureLog('error', "Failed to report user", { error });
      throw error;
    }
  },

  // Enhanced user profile creation with better error handling
  async createUserProfileIfNotExists(user: User): Promise<void> {
    try {
      const userRef = doc(db, "Users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        // Create new user profile (fallback for OAuth users)
        // Use consistent field handling with AuthContext
        // Firestore CREATE rule: allow create: if authed() && userId == uid()
        const sanitizedEmail = sanitizeEmail(user.email || '') || user.email || '';
        const emailUsername = sanitizedEmail.split('@')[0] || 'User';
        
        const userData = {
          uid: user.uid,
          email: sanitizedEmail,
          firstName: sanitizeString(user.displayName?.split(" ")[0] || '', 50) || 'User',
          lastName: sanitizeString(user.displayName?.split(" ").slice(1).join(" ") || '', 50) || null,
          displayName: sanitizeString(user.displayName || emailUsername, 100) || 'User',
          phoneNumber: sanitizeString(user.phoneNumber || '', 20) || null,
          username: '',
          profilePicture: sanitizeString(user.photoURL || '', 500) || null,
          role: "user",
          fcmToken: null,
          lastNotificationAt: null,
          unreadNotificationCount: 0,
          referrals: [],
          referredBy: null,
          shopName: null,
          loyaltyCoins: 0,
          tier: "Bronze",
          // Security: Initialize fields required by DELETE rule
          // DELETE rule requires: activeOrdersCount == 0 && kycStatus != 'verified'
          activeOrdersCount: 0, // Required for account deletion compliance
          kycStatus: null, // Required for account deletion compliance (null != 'verified')
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        // Use merge: true to avoid overwriting registration data
        await setDoc(userRef, userData, { merge: true });
        // Removed debug log to prevent sensitive data exposure
      } else {
        // Update existing profile if needed
        const userData = userSnap.data();
        const updates: any = {};

        if (!userData.email && user.email) {
          updates.email = user.email;
        }
        if (!userData.displayName && user.displayName) {
          updates.displayName = user.displayName;
        }
        if (!userData.photoURL && user.photoURL) {
          updates.photoURL = user.photoURL;
        }

        if (Object.keys(updates).length > 0) {
          updates.updatedAt = serverTimestamp();
          await updateDoc(userRef, updates);
          // Removed debug log to prevent sensitive data exposure
        }
      }
    } catch (error) {
      secureLog('error', "Error creating/updating user profile", { error });
      throw error;
    }
  },

  // Enhanced user presence management
  async updateUserPresence(uid: string, isOnline: boolean): Promise<void> {
    // Security: Guard against missing or invalid userId
    if (!uid || typeof uid !== 'string' || uid.trim() === '') {
      secureLog('warn', "[Presence] Skipping presence update — missing or invalid userId");
      return;
    }

    // Security: Validate document ID format
    if (!isValidDocumentId(uid)) {
      // SECURITY: Removed console.warn to prevent information leakage
      return;
    }

    try {
      const userRef = doc(db, "Users", uid); // ✅ Even segments: Users/{uid}
      await updateDoc(userRef, {
        presence: {
          status: isOnline ? "online" : "offline",
          lastSeen: serverTimestamp()
        },
        isOnline,
        lastSeen: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      secureLog('error', "[Presence] Failed to update user presence", { error });
    }
  },

  /**
   * migrateTopLevelMessagesToNested(conversationId?: string)
   * For each legacy `messages` doc (optionally filtered by conversationId),
   * creates/ensures the same doc under `conversations/{id}/messages/{newId}`,
   * copies fields (senderId, text, timestamp, readBy, createdAt, updatedAt),
   * and THEN deletes the legacy doc. This is manual/one-time. Do not auto-run.
   */
  async migrateTopLevelMessagesToNested(
    conversationId?: string
  ): Promise<void> {
    try {
      const legacyMessagesRef = collection(db, "messages");
      // NOTE: Composite index required: messages [conversationId: asc] + [timestamp: asc] (when conversationId filter is applied)
      // Covered in firestore.indexes.json
      let q = query(legacyMessagesRef, orderBy("timestamp", "asc"));

      if (conversationId) {
        q = query(q, where("conversationId", "==", conversationId));
      }

      const querySnapshot = await getDocs(q);
      // Removed debug log to prevent excessive console output

      const batch = writeBatch(db);
      let batchCount = 0;
      const maxBatchSize = 500; // Firestore batch limit

      for (const docSnapshot of querySnapshot.docs) {
        const data = docSnapshot.data();

        // Create new message in nested path
        const newMessageRef = doc(msgsCol(data.conversationId));
        const newMessageData = {
          senderId: data.senderId,
          text: data.text,
          timestamp: data.timestamp,
          readBy: data.readBy || [],
          createdAt: data.createdAt || safeServerTimestamp(),
          updatedAt: data.updatedAt || safeServerTimestamp(),
        };

        batch.set(newMessageRef, newMessageData);
        batch.delete(docSnapshot.ref);

        batchCount += 2; // Set + delete

        if (batchCount >= maxBatchSize) {
          await batch.commit();
          // Removed debug log to prevent excessive console output
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
        // Removed debug log to prevent excessive console output
      }

      // Removed debug log to prevent excessive console output
    } catch (error) {
      secureLog('error', "Migration failed", { error });
      throw error;
    }
  },

  // New required methods for messaging stack
  async listConversations(uid: string, limitN?: number, cursor?: any): Promise<Conversation[]> {
    try {
      const conversationsRef = collection(db, 'conversations');
      let q = query(
        conversationsRef,
        where('participants', 'array-contains', uid),
        orderBy('updatedAt', 'desc')
      );
      
      if (limitN) {
        q = query(q, limit(limitN));
      }
      
      if (cursor) {
        q = query(q, startAfter(cursor));
      }
      
      const querySnapshot = await getDocs(q);
      const conversations: Conversation[] = [];
      
      for (const doc of querySnapshot.docs) {
        const data = doc.data();
        const conversation: Conversation = {
          id: doc.id,
          participants: data.participants || [],
          lastMessage: data.lastMessage || '',
          lastMessageSenderId: data.lastMessageSenderId || '',
          lastMessageTime: data.lastMessageTime || safeServerTimestamp(),
          unreadCount: data.unreadCounts?.[uid] || 0,
          unreadCounts: data.unreadCounts || {},
          typingUserIds: data.typingUserIds || [],
          blockedBy: data.blockedBy || [],
          mutedBy: data.mutedBy || [],
          createdAt: data.createdAt || safeServerTimestamp(),
          updatedAt: data.updatedAt || safeServerTimestamp()
        };
        
        // Add user profile for the other participant
        const otherUserId = conversation.participants.find(participantUid => participantUid !== uid);
        if (otherUserId) {
          const userProfile = await this.getUserProfile(otherUserId);
          if (userProfile) {
            conversation.otherUser = userProfile;
          }
        }
        
        conversations.push(conversation);
      }
      
      return conversations;
    } catch (error) {
      secureLog('error', 'Failed to list conversations', { error });
      return [];
    }
  },

  async listMessages(conversationId: string, limitN?: number, cursor?: any): Promise<Message[]> {
    try {
      const messagesRef = collection(db, 'conversations', conversationId, 'messages');
      let q = query(
        messagesRef,
        orderBy('timestamp', 'desc')
      );
      
      if (limitN) {
        q = query(q, limit(limitN));
      }
      
      if (cursor) {
        q = query(q, startAfter(cursor));
      }
      
      const querySnapshot = await getDocs(q);
      const messages: Message[] = [];
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const message: Message = {
          id: doc.id,
          conversationId,
          senderId: data.senderId,
          text: data.text || '',
          imageUrl: data.imageUrl || null,
          fileUrl: data.fileUrl || null,
          timestamp: data.timestamp,
          readBy: data.readBy || [],
          isRead: data.readBy?.includes(auth.currentUser?.uid || '') || false
        };
        messages.push(message);
      });
      
      return messages;
    } catch (error) {
      secureLog('error', 'Failed to list messages', { error });
      return [];
    }
  },

  async sendMessageWithParams({ conversationId, senderId, receiverId, text, file, image }: {
    conversationId: string;
    senderId: string;
    receiverId: string;
    text: string;
    file?: File;
    image?: File;
  }): Promise<Message | null> {
    return this.sendMessage(conversationId, text, { file, image });
  },

  async markConversationRead(conversationId: string, uid: string): Promise<void> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);
      
      if (!conversationDoc.exists()) {
        throw new Error('Conversation not found');
      }
      
      const data = conversationDoc.data();
      const unreadCounts = data.unreadCounts || {};
      unreadCounts[uid] = 0;
      
      await updateDoc(conversationRef, {
        unreadCounts,
        updatedAt: safeServerTimestamp()
      });
    } catch (error) {
      secureLog('error', 'Failed to mark conversation as read', { error });
      throw error;
    }
  },

  async setTypingWithUid(conversationId: string, uid: string, isTyping: boolean): Promise<void> {
    try {
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);
      
      if (!conversationDoc.exists()) {
        return;
      }
      
      const data = conversationDoc.data();
      let typingUserIds = data.typingUserIds || [];
      
      if (isTyping) {
        if (!typingUserIds.includes(uid)) {
          typingUserIds.push(uid);
        }
      } else {
        typingUserIds = typingUserIds.filter((id: string) => id !== uid);
      }
      
      await updateDoc(conversationRef, {
        typingUserIds,
        updatedAt: safeServerTimestamp()
      });
    } catch (error) {
      secureLog('error', 'Failed to set typing status', { error });
    }
  },

  async setPresence(isOnline: boolean): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      secureLog('warn', "[Presence] Cannot set presence — user not authenticated");
      return;
    }
    return this.updateUserPresence(uid, isOnline);
  },

  async blockUserInConversation(conversationId: string, sellerId: string): Promise<void> {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error('User must be authenticated to block users');
      }
      
      // Add to blocked users subcollection
      const blockedUserRef = doc(db, 'Users', userId, 'blockedUsers', sellerId);
      await setDoc(blockedUserRef, {
        blockedAt: safeServerTimestamp(),
        blockedUserId: sellerId
      });
      
      // Update conversation blockedBy array
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationDoc = await getDoc(conversationRef);
      
      if (conversationDoc.exists()) {
        const data = conversationDoc.data();
        const blockedBy = data.blockedBy || [];
        if (!blockedBy.includes(sellerId)) {
          blockedBy.push(sellerId);
        }
        
        await updateDoc(conversationRef, {
          blockedBy,
          updatedAt: safeServerTimestamp()
        });
      }
    } catch (error) {
      secureLog('error', 'Failed to block user', { error });
      throw error;
    }
  }
};


// Re-export Firebase auth functions
export {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  signInWithCustomToken,
};

// Re-export types from types/index.ts
export type { Product, FlashSaleProduct } from "../types";

export type { User };

// Re-export order types individually
export type {
  OrderData,
  ShippingAddress,
  BillingAddress,
  Order,
  CoinTransaction,
  OrderItem,
} from "../types/order";

// Coin Service
export {
  getUserCoins,
  getCoinHistory,
  getTierForCoins,
  getTierProgress,
  getTransactionTypeStyle,
  formatTransactionDate,
  TIER_CONFIG,
} from "./firebase/loyaltyCoinsService";
export type {
  CoinTransaction as CoinTransactionType,
  UserCoins,
  TierInfo,
} from "./firebase/loyaltyCoinsService";

// Advertisement Analytics Service
export async function recordAdView(adId: string) {
  try {
    if (!adId) return;
    const fn = httpsCallable(cloudFunctions, "recordAdEvent");
    await fn({ adId, event: "view" });
  } catch (e) {
    // Only log in development, suppress in production
    if (process.env.NODE_ENV === "development") {
      secureLog('warn', "[ad] view failed", { error: e });
    }
    // Don't throw error to prevent app crashes
  }
}

export async function recordAdClick(adId: string) {
  try {
    if (!adId) return;
    const fn = httpsCallable(cloudFunctions, "recordAdEvent");
    await fn({ adId, event: "click" });
  } catch (e) {
    // Only log in development, suppress in production
    if (process.env.NODE_ENV === "development") {
      secureLog('warn', "[ad] click failed", { error: e });
    }
    // Don't throw error to prevent app crashes
  }
}

// Report Service
export { reportService } from "./firebase/productReportService";
export type {
  FalseProductReport,
  ReportFormData,
} from "./firebase/productReportService";

// Trace Service
export { traceService } from "./firebase/productTraceService";
export type {
  TraceLog,
  TraceLogType,
  TraceLogRole,
} from "./firebase/productTraceService";

// Helper function to update product rating in database
export const updateProductRating = async (productId: string): Promise<void> => {
  try {
    // Import the review stats function
    const { getReviewStats } = await import("./reviews");
    const stats = await getReviewStats(productId);

    const productRef = doc(productsCollection, productId);
    await updateDoc(productRef, {
      rating: stats.averageRating,
      totalReviews: stats.totalReviews,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    // SECURITY: Removed console.error to prevent information leakage
  }
};

// Function to update all product ratings in the database
export const updateAllProductRatings = async (): Promise<void> => {
  try {
    const querySnapshot = await getDocs(productsCollection);
    const products = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    let updated = 0;
    let errors = 0;

    for (const product of products) {
      try {
        await updateProductRating(product.id);
        updated++;

      } catch (error) {
        secureLog('error',
          `Failed to update rating for product ${product.id}:`,
          error
        );
        errors++;
      }
    }

    // Removed debug log to prevent excessive console output
  } catch (error) {
    secureLog('error', "Failed to update all product ratings", { error });
  }
};

